import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import standBackgroundUrl from '../assets/stand.png';
import { DEFAULT_PERSONA_TRANSFORM, normalizePersonaTransformFromDb } from '../utils/personaHotspotTransform';
import './ConstructorAdmin.css';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5001';
const buildImageUrl = (ruta) => {
  if (!ruta) return '';
  if (String(ruta).startsWith('http')) return ruta;
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  return base + (String(ruta).startsWith('/') ? ruta : '/' + ruta);
};

const isVideoAsset = (ruta) => {
  const src = String(ruta || '').toLowerCase();
  return src.endsWith('.webm') || src.endsWith('.mp4') || src.includes('.webm?') || src.includes('.mp4?');
};

const TIPOS_PANORAMA = [
  { value: 'calle', label: 'Calle/Exterior' },
  { value: 'entrada', label: 'Entrada' },
  { value: 'pasillo', label: 'Pasillo' },
  { value: 'local', label: 'Local/Tienda' },
  { value: 'escalera', label: 'Escalera' },
  { value: 'ascensor', label: 'Ascensor' },
];

const etiquetaTipoPanorama = (tipo) =>
  TIPOS_PANORAMA.find((o) => o.value === tipo)?.label || (tipo ? String(tipo) : 'Pasillo');

const TIPOS_HOTSPOT = [
  { value: 'hotspot-punto', label: 'Punto (círculo verde)' },
  { value: 'hotspot-flecha-pulse', label: 'Flecha (siguiente escena)' },
  { value: 'hotspot-burbuja', label: 'Burbuja (vista previa de la siguiente escena)' },
  { value: 'hotspot-caja-logo', label: 'Logo / faldón de stand (blanco, transformación libre)' },
  { value: 'hotspot-persona', label: 'Persona/objeto en escena (PNG, GIF o WEBM)' },
  { value: 'hotspot-valla', label: 'Marquesina MUPI (marco negro, pilar central + enlace)' },
];

const COLORES_HOTSPOT = [
  { value: '', label: 'Verde (por defecto)' },
  { value: 'azul', label: 'Azul' },
  { value: 'blanco', label: 'Blanco' },
  { value: 'rojo', label: 'Rojo' },
  { value: 'negro', label: 'Negro' },
];

const ESTILOS_FLECHA = [
  { value: 'simple', label: 'Simple (recomendada)' },
  { value: 'ancha', label: 'Ancha' },
  { value: 'compacta', label: 'Compacta' },
];

const DIRECCIONES_FLECHA = [
  { value: 'derecha', label: 'Derecha →' },
  { value: 'arriba', label: 'Arriba ↑' },
  { value: 'izquierda', label: 'Izquierda ←' },
  { value: 'abajo', label: 'Abajo ↓' },
  { value: 'arriba-derecha', label: 'Diagonal ↗' },
  { value: 'arriba-izquierda', label: 'Diagonal ↖' },
  { value: 'abajo-derecha', label: 'Diagonal ↘' },
  { value: 'abajo-izquierda', label: 'Diagonal ↙' },
];

const DEFAULT_FLECHA_TRANSFORM = {
  rotateDelta: 0,
  scaleX: 1,
  scaleY: 0.62,
  skewX: 0,
  skewY: 0,
  perspectiveX: 0,
  perspectiveY: 0,
  offsetX: 0,
  offsetY: 0
};

/** Caja logo tipo stand modular (faldón + base con suelo) */
/** Stand punto de venta: tamaño por defecto más grande, proporción cercana al JPG */
const LOGO_FASCIA_ANCHO_DEFAULT = 560;
const LOGO_FASCIA_ALTO_DEFAULT = 300;
/** Persona/objeto: caja de contenido (no el círculo verde de punto) */
const PERSONA_ANCHO_DEFAULT = 220;
const PERSONA_ALTO_DEFAULT = 360;
/** Límite de tamaño del contenedor en el constructor (evita zona clicable enorme) */
const LOGO_CONSTRUCTOR_ANCHO_MAX = 720;
const LOGO_CONSTRUCTOR_ALTO_MAX = 520;
const PERSONA_CONSTRUCTOR_ANCHO_MAX = 480;
const PERSONA_CONSTRUCTOR_ALTO_MAX = 560;

function applyFlechaCssVarsToElements(elements, t, defaultScaleY) {
  elements.forEach((el) => {
    if (!el || !el.classList) return;
    el.style.setProperty('--flecha-rotate-delta', `${Number(t.rotateDelta) || 0}deg`);
    el.style.setProperty('--flecha-scale-x', `${Number(t.scaleX) || 1}`);
    el.style.setProperty('--flecha-scale-y', `${Number(t.scaleY) ?? defaultScaleY}`);
    el.style.setProperty('--flecha-skew-x', `${Number(t.skewX) || 0}deg`);
    el.style.setProperty('--flecha-skew-y', `${Number(t.skewY) || 0}deg`);
    const perspPxX = Number(t.perspectiveX) || 0;
    const perspPxY = Number(t.perspectiveY) || 0;
    el.style.setProperty('--flecha-perspective-x', `${perspPxX}px`);
    el.style.setProperty('--flecha-perspective-y', `${perspPxY}px`);
    el.style.setProperty('--flecha-persp-rot-x', `${-perspPxY * 0.9}deg`);
    el.style.setProperty('--flecha-persp-rot-y', `${perspPxX * 0.9}deg`);
    el.style.setProperty('--flecha-offset-x', `${Number(t.offsetX) || 0}px`);
    el.style.setProperty('--flecha-offset-y', `${Number(t.offsetY) || 0}px`);
  });
}

const ConstructorAdmin = () => {
  const navigate = useNavigate();
  const [centros, setCentros] = useState([]);
  const [centroId, setCentroId] = useState(null);
  const [plantas, setPlantas] = useState([]);
  const [panoramas, setPanoramas] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editPanorama, setEditPanorama] = useState(null);
  const [nuevoHotspot, setNuevoHotspot] = useState({ 
  pitch: 0, 
  yaw: 0, 
  css_class: 'hotspot-punto', 
  color: '', 
  flecha_estilo: 'simple',
  flecha_direccion: 'derecha',
  flecha_transform: { ...DEFAULT_FLECHA_TRANSFORM },
  url_externa: '',
  titulo: '',
  nombre: '',
  descripcion: '',
  link: '',
  telefono: '',
  telefono2: '',
  direccion: '',
  email: '',
  imagen: '',
  galeria: [],
  tieneNavegacion: false,
  panorama_destino_id: null,
  ancho: 300,
  alto: 200,
  scale_visual: 2.5
});
const [subiendoImagenHotspot, setSubiendoImagenHotspot] = useState(false);
  /** ID del hotspot que se está editando (null = crear nuevo) */
  const [editingHotspotId, setEditingHotspotId] = useState(null);
  const [pannellumReady, setPannellumReady] = useState(!!window.pannellum);
  const [tab, setTab] = useState('panoramas');
  const [nuevaPlanta, setNuevaPlanta] = useState({ nombre: '', nivel: 0 });
  const [nuevaTienda, setNuevaTienda] = useState({ nombre: '', panorama_id: '', planta_id: '', pitch: 0, yaw: 0 });
  const [nuevoElemento, setNuevoElemento] = useState({ tipo: 'elemento', nombre: '', descripcion: '', pitch: 0, yaw: 0 });
  const [uploadTipo, setUploadTipo] = useState('pasillo');
  const [uploadPlantaId, setUploadPlantaId] = useState('');
  const [viewerLoadedTrigger, setViewerLoadedTrigger] = useState(0);
  const viewerRef = useRef(null);
  const pannellumRef = useRef(null);
  const transformingRef = useRef(false);
  /** Ref con la última posición clicada en el panorama (evita que se pierdan pitch/yaw si el estado no ha actualizado al enviar) */
  const lastClickCoordsRef = useRef({ pitch: 0, yaw: 0 });
  /** Vista al entrar a la escena (transición / carga) */
  const [entradaVista, setEntradaVista] = useState({ pitch: 0, yaw: 0, hfov: 120 });
  const [dragPanoramaIdx, setDragPanoramaIdx] = useState(null);
  const dragHotspotIdRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('route-constructor');
    return () => document.body.classList.remove('route-constructor');
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    api.get('/api/centros').then(r => r.data.success && setCentros(r.data.centros || []));
  }, []);

  useEffect(() => {
    if (!editPanorama) return;
    const raw = editPanorama.entrada_vista_json;
    if (raw) {
      try {
        const j = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setEntradaVista({
          pitch: Number(j.pitch) || 0,
          yaw: Number(j.yaw) || 0,
          hfov: Math.min(120, Math.max(50, Number(j.hfov) || 120))
        });
      } catch (_) {
        setEntradaVista({ pitch: 0, yaw: 0, hfov: 120 });
      }
    } else {
      setEntradaVista({ pitch: 0, yaw: 0, hfov: 120 });
    }
  }, [editPanorama?.id, editPanorama?.entrada_vista_json]);

  const panoramasOrdenados = useMemo(
    () =>
      [...panoramas].sort(
        (a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0) || a.id - b.id
      ),
    [panoramas]
  );

  const plantaPorId = useMemo(() => {
    const m = new Map();
    (plantas || []).forEach((pl) => m.set(Number(pl.id), pl));
    return m;
  }, [plantas]);

  const etiquetaPanoramaLarga = useCallback(
    (p) => {
      if (!p) return '';
      const pl = p.planta_id != null && p.planta_id !== '' ? plantaPorId.get(Number(p.planta_id)) : null;
      const plantaTxt = pl?.nombre || 'Sin planta';
      const tipoTxt = etiquetaTipoPanorama(p.tipo);
      const nom = (p.nombre && String(p.nombre).trim()) || `Panorama ${p.id}`;
      return `${plantaTxt} · ${tipoTxt} · ${nom}`;
    },
    [plantaPorId]
  );

  const etiquetaPanoramaSelect = useCallback(
    (p) => `${etiquetaPanoramaLarga(p)} · id ${p.id}`,
    [etiquetaPanoramaLarga]
  );

  const bloquesListaPanoramas = useMemo(() => {
    const byPlanta = new Map();
    const sin = [];
    panoramasOrdenados.forEach((p) => {
      if (p.planta_id == null || p.planta_id === '') {
        sin.push(p);
        return;
      }
      const id = Number(p.planta_id);
      if (!byPlanta.has(id)) byPlanta.set(id, []);
      byPlanta.get(id).push(p);
    });
    const plantasSorted = [...plantas].sort(
      (a, b) => (Number(a.nivel) || 0) - (Number(b.nivel) || 0) || a.id - b.id
    );
    const idsEnCatalogo = new Set(plantasSorted.map((pl) => Number(pl.id)));
    const out = [];
    plantasSorted.forEach((pl) => {
      const items = byPlanta.get(Number(pl.id));
      if (items && items.length > 0) {
        out.push({ type: 'header', key: `h-pl-${pl.id}`, label: pl.nombre });
        items.forEach((pan) => out.push({ type: 'pano', key: `p-${pan.id}`, p: pan }));
      }
    });
    [...byPlanta.keys()]
      .filter((id) => !idsEnCatalogo.has(id))
      .sort((a, b) => a - b)
      .forEach((plId) => {
        const items = byPlanta.get(plId);
        if (items && items.length > 0) {
          out.push({ type: 'header', key: `h-orphan-${plId}`, label: `Planta (referencia id ${plId})` });
          items.forEach((pan) => out.push({ type: 'pano', key: `p-${pan.id}`, p: pan }));
        }
      });
    if (sin.length > 0) {
      out.push({ type: 'header', key: 'h-sin-planta', label: 'Sin planta asignada' });
      sin.forEach((pan) => out.push({ type: 'pano', key: `p-${pan.id}`, p: pan }));
    }
    return out;
  }, [panoramasOrdenados, plantas]);

  const usarVistaActualComoEntrada = () => {
    const v = pannellumRef.current;
    if (!v) return;
    try {
      const pitch = typeof v.getPitch === 'function' ? v.getPitch() : 0;
      const yaw = typeof v.getYaw === 'function' ? v.getYaw() : 0;
      const hfov = typeof v.getHfov === 'function' ? v.getHfov() : 120;
      setEntradaVista({
        pitch,
        yaw,
        hfov: Math.min(120, Math.max(50, hfov))
      });
    } catch (_) {
      alert('No se pudo leer la vista del visor.');
    }
  };

  const guardarVistaEntrada = async () => {
    if (!editPanorama || !centroId) return;
    const json = JSON.stringify({
      pitch: Number(entradaVista.pitch) || 0,
      yaw: Number(entradaVista.yaw) || 0,
      hfov: Math.min(120, Math.max(50, Number(entradaVista.hfov) || 120))
    });
    try {
      await api.put(`/api/panoramas/${editPanorama.id}`, {
        nombre: editPanorama.nombre,
        planta_id: editPanorama.planta_id,
        tipo: editPanorama.tipo || 'pasillo',
        orden: editPanorama.orden ?? 0,
        entrada_vista_json: json
      });
      await cargarCentro(centroId);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error al guardar la vista');
    }
  };

  const limpiarVistaEntrada = async () => {
    if (!editPanorama || !centroId) return;
    try {
      await api.put(`/api/panoramas/${editPanorama.id}`, {
        nombre: editPanorama.nombre,
        planta_id: editPanorama.planta_id,
        tipo: editPanorama.tipo || 'pasillo',
        orden: editPanorama.orden ?? 0,
        entrada_vista_json: null
      });
      await cargarCentro(centroId);
      setEntradaVista({ pitch: 0, yaw: 0, hfov: 120 });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error');
    }
  };

  const guardarDatosPanoramaActivo = async () => {
    if (!editPanorama || !centroId) return;
    try {
      await api.put(`/api/panoramas/${editPanorama.id}`, {
        nombre: (editPanorama.nombre || '').trim() || `Panorama ${editPanorama.id}`,
        planta_id:
          editPanorama.planta_id === '' || editPanorama.planta_id == null
            ? null
            : Number(editPanorama.planta_id),
        tipo: editPanorama.tipo || 'pasillo',
        orden: Number(editPanorama.orden) || 0
      });
      await cargarCentro(centroId);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error al guardar los datos');
    }
  };

  const reordenarPanoramas = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || !centroId) return;
    const list = [...panoramasOrdenados];
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    try {
      await Promise.all(
        list.map((p, i) =>
          api.put(`/api/panoramas/${p.id}`, {
            nombre: p.nombre,
            planta_id: p.planta_id,
            tipo: p.tipo || 'pasillo',
            orden: i
          })
        )
      );
      await cargarCentro(centroId);
    } catch (err) {
      alert(err.response?.data?.message || 'Error al reordenar panoramas');
    }
  };

  const reordenarHotspots = async (fromIndex, toIndex) => {
    if (!editPanorama || fromIndex === toIndex) return;
    const sorted = hotspots
      .filter((h) => h.panorama_id === editPanorama.id)
      .sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0) || a.id - b.id);
    const ids = sorted.map((h) => h.id);
    const [id] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, id);
    try {
      await api.put('/api/hotspots/reorder', {
        panorama_id: editPanorama.id,
        ids
      });
      await cargarCentro(centroId);
    } catch (err) {
      alert(err.response?.data?.message || 'Error al reordenar hotspots');
    }
  };

  const cargarCentro = async (id) => {
    setCentroId(id);
    try {
      const res = await api.get(`/api/centros/${id}`);
      if (res.data.success) {
        setPlantas(res.data.centro.plantas || []);
        setPanoramas(res.data.centro.panoramas || []);
        setTiendas(res.data.centro.tiendas || []);
        setHotspots(res.data.centro.hotspots || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!editPanorama?.id) return;
    const p = panoramas.find((x) => Number(x.id) === Number(editPanorama.id));
    if (p) setEditPanorama(p);
  }, [panoramas, editPanorama?.id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !centroId) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('imagen', file);
    formData.append('centro_id', centroId);
    formData.append('nombre', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('tipo', uploadTipo);
    if (uploadPlantaId) formData.append('planta_id', uploadPlantaId);
    try {
      await api.post('/api/panoramas', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      cargarCentro(centroId);
    } catch (err) {
      alert(err.response?.data?.message || 'Error al subir');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleAddPlanta = async () => {
    if (!centroId || !nuevaPlanta.nombre.trim()) return;
    try {
      await api.post('/api/plantas', { centro_id: centroId, nombre: nuevaPlanta.nombre.trim(), nivel: Number(nuevaPlanta.nivel) || 0 });
      cargarCentro(centroId);
      setNuevaPlanta({ nombre: '', nivel: 0 });
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleAddTienda = async () => {
    if (!centroId || !nuevaTienda.nombre.trim()) return;
    try {
      const res = await api.post('/api/tiendas', {
        centro_id: centroId,
        nombre: nuevaTienda.nombre.trim(),
        planta_id: nuevaTienda.planta_id || null,
        panorama_id: nuevaTienda.panorama_id || null,
        pitch: Number(nuevaTienda.pitch) || 0,
        yaw: Number(nuevaTienda.yaw) || 0
      });
      if (nuevaTienda.panorama_id && res.data.id) {
        await api.post('/api/hotspots', {
          panorama_id: nuevaTienda.panorama_id,
          nombre: nuevaTienda.nombre.trim(),
          pitch: Number(nuevaTienda.pitch) || 0,
          yaw: Number(nuevaTienda.yaw) || 0,
          tipo: 'info',
          tienda_id: res.data.id
        });
      }
      setNuevaTienda({ nombre: '', panorama_id: '', planta_id: '', pitch: 0, yaw: 0 });
      cargarCentro(centroId);
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleAddElemento = async () => {
    if (!editPanorama || !nuevoElemento.nombre.trim()) return;
    try {
      await api.post('/api/hotspots', {
        panorama_id: editPanorama.id,
        nombre: nuevoElemento.nombre.trim(),
        descripcion: nuevoElemento.descripcion || null,
        pitch: Number(nuevoElemento.pitch) || 0,
        yaw: Number(nuevoElemento.yaw) || 0,
        tipo: nuevoElemento.tipo
      });
      cargarCentro(centroId);
      setNuevoElemento(prev => ({ ...prev, nombre: '', descripcion: '' }));
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleUploadImagenHotspot = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !centroId) {
      alert('Selecciona un archivo y asegúrate de tener un centro seleccionado');
      return;
    }
    setSubiendoImagenHotspot(true);
    const formData = new FormData();
    formData.append('imagen', file);
    formData.append('centro_id', centroId);
    try {
      const res = await api.post('/api/hotspots/upload-imagen', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success && res.data.ruta_archivo) {
        console.log('Archivo subido correctamente:', res.data.ruta_archivo);
        setNuevoHotspot(prev => ({ ...prev, imagen: res.data.ruta_archivo }));
      } else {
        alert('Error: No se recibió la ruta del archivo');
      }
    } catch (err) {
      console.error('Error al subir archivo:', err);
      alert(err.response?.data?.message || 'Error al subir archivo: ' + err.message);
    }
    setSubiendoImagenHotspot(false);
    e.target.value = '';
  };

  const handleAddGaleriaImagen = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !centroId) return;
    setSubiendoImagenHotspot(true);
    const formData = new FormData();
    formData.append('imagen', file);
    formData.append('centro_id', centroId);
    try {
      const res = await api.post('/api/hotspots/upload-imagen', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success && res.data.ruta_archivo) {
        setNuevoHotspot(prev => ({ ...prev, galeria: [...(prev.galeria || []), res.data.ruta_archivo] }));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error al subir imagen');
    }
    setSubiendoImagenHotspot(false);
    e.target.value = '';
  };

  const buildHotspotCssClass = (state) => {
    const src = state || {};
    const esFlecha = src.css_class === 'hotspot-flecha-pulse';
    const esValla = src.css_class === 'hotspot-valla';
    const esBurbuja = src.css_class === 'hotspot-burbuja';
    let cssClass = src.css_class || 'hotspot-punto';
    if (esBurbuja) return 'hotspot-burbuja';
    if (esFlecha) {
      const estilo = (src.flecha_estilo || 'simple').trim();
      const direccion = (src.flecha_direccion || 'derecha').trim();
      if (estilo === 'ancha') cssClass += ' hotspot-flecha-ancha';
      else if (estilo === 'compacta') cssClass += ' hotspot-flecha-compacta';
      cssClass += ` hotspot-dir-${direccion}`;
      cssClass += ' hotspot-flecha-suelo';
    }
    if (src.color && src.color.trim()) cssClass += ` hotspot-${src.color.trim()}`;
    if (esValla) cssClass = 'hotspot-valla';
    return cssClass;
  };

  const handleAddHotspot = async () => {
    if (!editPanorama) return;
    
    const esFlecha = nuevoHotspot.css_class === 'hotspot-flecha-pulse';
    const esBurbuja = nuevoHotspot.css_class === 'hotspot-burbuja';
    const esPunto = nuevoHotspot.css_class === 'hotspot-punto';
    const esLogo = nuevoHotspot.css_class === 'hotspot-caja-logo';
    const esPersona = nuevoHotspot.css_class === 'hotspot-persona';
    const esValla = nuevoHotspot.css_class === 'hotspot-valla';
    
    // Flecha y burbuja requieren navegación
    if ((esFlecha || esBurbuja) && !nuevoHotspot.panorama_destino_id) {
      alert(esBurbuja ? 'La burbuja requiere un panorama destino para la vista previa' : 'La flecha requiere un destino de navegación');
      return;
    }
    
    // Valla requiere imagen
    if (esValla && !nuevoHotspot.imagen) {
      alert('La valla publicitaria requiere una imagen subida');
      return;
    }
    
    // Punto y logo pueden tener info sin navegación
    const tieneNavegacion = nuevoHotspot.tieneNavegacion && nuevoHotspot.panorama_destino_id;
    const tieneInfo = nuevoHotspot.titulo || nuevoHotspot.nombre || nuevoHotspot.descripcion || nuevoHotspot.link || nuevoHotspot.telefono || nuevoHotspot.telefono2 || nuevoHotspot.direccion || nuevoHotspot.email || nuevoHotspot.imagen || (nuevoHotspot.galeria && nuevoHotspot.galeria.length > 0);
    
    if (!esValla && !esBurbuja && !tieneNavegacion && !tieneInfo) {
      alert('Debes añadir información o un destino de navegación');
      return;
    }
    
    if (esLogo && !nuevoHotspot.imagen) {
      alert('El logo requiere una imagen subida');
      return;
    }
    // Priorizar ref (último clic) para no perder coordenadas si el estado no ha actualizado aún
    const pitchEnviar = lastClickCoordsRef.current.pitch ?? Number(nuevoHotspot.pitch) ?? 0;
    const yawEnviar = lastClickCoordsRef.current.yaw ?? Number(nuevoHotspot.yaw) ?? 0;
    if (esPersona && !nuevoHotspot.imagen) {
      alert('La persona/objeto en escena requiere un archivo visual (PNG, GIF o WEBM).');
      return;
    }
    
    try {
      // Construir css_class: base + estilo/color
      let cssClass = buildHotspotCssClass(nuevoHotspot);
      // Tipo explícito por clase para evitar ambigüedad en el visor
      const tipo = esFlecha || esBurbuja
        ? 'navegacion'
        : (esValla ? 'valla' : (esPersona ? 'persona' : (tieneNavegacion ? 'navegacion' : 'informativo')));
      
      console.log('Guardando hotspot:', {
        css_class: cssClass,
        tipo: tipo,
        pitch: pitchEnviar,
        yaw: yawEnviar,
        imagen: nuevoHotspot.imagen,
        panorama_destino_id: tieneNavegacion ? parseInt(nuevoHotspot.panorama_destino_id, 10) : null,
        titulo: nuevoHotspot.titulo,
        nombre: nuevoHotspot.nombre
      });
      
      await api.post('/api/hotspots', {
        panorama_id: parseInt(editPanorama.id, 10),
        nombre: nuevoHotspot.nombre || null,
        titulo: nuevoHotspot.titulo || null,
        descripcion: nuevoHotspot.descripcion || null,
        pitch: pitchEnviar,
        yaw: yawEnviar,
        tipo: tipo,
        panorama_destino_id: (tieneNavegacion || esBurbuja) ? parseInt(nuevoHotspot.panorama_destino_id, 10) : null,
        css_class: cssClass,
        imagen: nuevoHotspot.imagen || null,
        link: nuevoHotspot.link || null,
        link_panorama: nuevoHotspot.link_panorama || null,
        telefono: nuevoHotspot.telefono || null,
        telefono2: nuevoHotspot.telefono2 || null,
        direccion: nuevoHotspot.direccion || null,
        email: nuevoHotspot.email || null,
        galeria: nuevoHotspot.galeria?.length ? JSON.stringify(nuevoHotspot.galeria) : null,
        transform_json: (esFlecha || esValla || esLogo || esPersona)
          ? JSON.stringify(
              esPersona
                ? normalizePersonaTransformFromDb(nuevoHotspot.flecha_transform || {})
                : (nuevoHotspot.flecha_transform || DEFAULT_FLECHA_TRANSFORM)
            )
          : null,
        ancho: esValla ? (Number(nuevoHotspot.ancho) || 300) : esLogo ? (Number(nuevoHotspot.ancho) || LOGO_FASCIA_ANCHO_DEFAULT) : esPersona ? (Number(nuevoHotspot.ancho) || PERSONA_ANCHO_DEFAULT) : null,
        alto: esValla ? (Number(nuevoHotspot.alto) || 200) : esLogo ? (Number(nuevoHotspot.alto) || LOGO_FASCIA_ALTO_DEFAULT) : esPersona ? (Number(nuevoHotspot.alto) || PERSONA_ALTO_DEFAULT) : null,
        scale_visual: esValla ? (Number(nuevoHotspot.scale_visual) || 2.5) : null
      });
      cargarCentro(centroId);
      setEditingHotspotId(null);
      lastClickCoordsRef.current = { pitch: 0, yaw: 0 };
      setNuevoHotspot({ 
        pitch: 0, 
        yaw: 0, 
        css_class: 'hotspot-punto', 
        color: '', 
        flecha_estilo: 'simple',
        flecha_direccion: 'derecha',
        flecha_transform: { ...DEFAULT_FLECHA_TRANSFORM },
        url_externa: '',
        titulo: '',
        nombre: '',
        descripcion: '',
        link: '',
        telefono: '',
        telefono2: '',
        direccion: '',
        email: '',
        imagen: '',
        galeria: [],
        tieneNavegacion: false,
        panorama_destino_id: null,
        ancho: 300,
        alto: 200,
        scale_visual: 2.5
      });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error al añadir hotspot');
    }
  };

  const handleClickPanorama = (e) => {
    if (!pannellumRef.current || !editPanorama) return;
    if (transformingRef.current) return;
    if (typeof pannellumRef.current.isLoaded === 'function' && !pannellumRef.current.isLoaded()) return;
    try {
      const coords = pannellumRef.current.mouseEventToCoords(e.nativeEvent);
      if (Array.isArray(coords) && coords.length >= 2) {
        const [pitch, yaw] = coords;
        lastClickCoordsRef.current = { pitch, yaw };
        setNuevoHotspot(prev => ({ ...prev, pitch, yaw }));
        if (tab === 'elementos') setNuevoElemento(prev => ({ ...prev, pitch, yaw }));
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (window.pannellum) {
      setPannellumReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = '/pannellum.js';
    script.async = true;
    script.onload = () => setPannellumReady(true);
    document.head.appendChild(script);
  }, []);

  const draftLpFingerprint = useMemo(
    () =>
      !editingHotspotId && nuevoHotspot.imagen && (nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona')
        ? `${nuevoHotspot.imagen}|${nuevoHotspot.css_class}|${Number(nuevoHotspot.ancho)}|${Number(nuevoHotspot.alto)}`
        : '',
    [editingHotspotId, nuevoHotspot.imagen, nuevoHotspot.css_class, nuevoHotspot.ancho, nuevoHotspot.alto]
  );

  useEffect(() => {
    if (!pannellumReady || !editPanorama || !viewerRef.current || !window.pannellum) return;
    const p = panoramas.find(x => x.id === editPanorama.id);
    if (!p?.ruta_archivo) return;
    const url = p.ruta_archivo.startsWith('http') ? p.ruta_archivo : (API_BASE + (p.ruta_archivo.startsWith('/') ? '' : '/') + p.ruta_archivo);
    if (pannellumRef.current) try { pannellumRef.current.destroy(); } catch (e) {}
    pannellumRef.current = null;
    viewerRef.current.innerHTML = '';
    const hotSpotsList = [];
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const parseTransform = (raw) => {
      try {
        let value = raw;
        for (let i = 0; i < 2; i++) {
          if (typeof value === 'string' && value.trim()) value = JSON.parse(value);
        }
        const parsed = (value && typeof value === 'object') ? value : {};
        return { ...DEFAULT_FLECHA_TRANSFORM, ...parsed };
      } catch (_) {
        return { ...DEFAULT_FLECHA_TRANSFORM };
      }
    };
    const clampedLogoPersonaSize = (isLogo, isEditing, hotspot) => {
      const defW = isLogo ? LOGO_FASCIA_ANCHO_DEFAULT : PERSONA_ANCHO_DEFAULT;
      const defH = isLogo ? LOGO_FASCIA_ALTO_DEFAULT : PERSONA_ALTO_DEFAULT;
      const maxW = isLogo ? LOGO_CONSTRUCTOR_ANCHO_MAX : PERSONA_CONSTRUCTOR_ANCHO_MAX;
      const maxH = isLogo ? LOGO_CONSTRUCTOR_ALTO_MAX : PERSONA_CONSTRUCTOR_ALTO_MAX;
      let w = isEditing ? (Number(nuevoHotspot.ancho) || defW) : (Number(hotspot.ancho) || defW);
      let hVal = isEditing ? (Number(nuevoHotspot.alto) || defH) : (Number(hotspot.alto) || defH);
      w = Math.max(40, Math.min(maxW, w));
      hVal = Math.max(40, Math.min(maxH, hVal));
      return { w, h: hVal };
    };
    const applyTransformVars = (el, tr) => {
      if (!el) return;
      const t = { ...DEFAULT_FLECHA_TRANSFORM, ...(tr || {}) };
      el.style.setProperty('--flecha-rotate-delta', `${Number(t.rotateDelta) || 0}deg`);
      el.style.setProperty('--flecha-scale-x', `${Number(t.scaleX) || 1}`);
      el.style.setProperty('--flecha-scale-y', `${Number(t.scaleY) || 0.62}`);
      el.style.setProperty('--flecha-skew-x', `${Number(t.skewX) || 0}deg`);
      el.style.setProperty('--flecha-skew-y', `${Number(t.skewY) || 0}deg`);
      const perspPxX = Number(t.perspectiveX) || 0;
      const perspPxY = Number(t.perspectiveY) || 0;
      el.style.setProperty('--flecha-perspective-x', `${perspPxX}px`);
      el.style.setProperty('--flecha-perspective-y', `${perspPxY}px`);
      el.style.setProperty('--flecha-persp-rot-x', `${-perspPxY * 0.9}deg`);
      el.style.setProperty('--flecha-persp-rot-y', `${perspPxX * 0.9}deg`);
      el.style.setProperty('--flecha-offset-x', `${Number(t.offsetX) || 0}px`);
      el.style.setProperty('--flecha-offset-y', `${Number(t.offsetY) || 0}px`);
    };
    const arrowClassTokens = [
      'hotspot-flecha-ancha', 'hotspot-flecha-compacta',
      'hotspot-dir-derecha', 'hotspot-dir-arriba', 'hotspot-dir-izquierda', 'hotspot-dir-abajo',
      'hotspot-dir-arriba-derecha', 'hotspot-dir-arriba-izquierda', 'hotspot-dir-abajo-derecha', 'hotspot-dir-abajo-izquierda',
      'hotspot-azul', 'hotspot-rojo', 'hotspot-blanco', 'hotspot-negro'
    ];
    const applyCssClassesToHotspotChain = (el, cssClassStr) => {
      if (!el || !cssClassStr) return;
      const classes = String(cssClassStr).split(/\s+/).filter(Boolean);
      arrowClassTokens.forEach(c => el.classList.remove(c));
      classes.forEach(c => el.classList.add(c));
      let p = el.parentElement;
      let d = 0;
      while (p && d < 6) {
        if (p.classList && (p.classList.contains('pnlm-hotspot-base') || p.classList.contains('pnlm-hotspot'))) {
          arrowClassTokens.forEach(c => p.classList.remove(c));
          classes.forEach(c => p.classList.add(c));
        }
        p = p.parentElement;
        d++;
      }
    };
    const applyTransformVarsToHotspotChain = (el, tr) => {
      if (!el) return;
      applyTransformVars(el, tr);
      let p = el.parentElement;
      let d = 0;
      while (p && d < 6) {
        if (p.classList && (p.classList.contains('pnlm-hotspot-base') || p.classList.contains('pnlm-hotspot'))) {
          applyTransformVars(p, tr);
        }
        p = p.parentElement;
        d++;
      }
    };
    const cssBase = (h) => (h.css_class && String(h.css_class).trim()) ? String(h.css_class).trim() : 'hotspot-punto';
    const hotspotsActuales = hotspots.filter(h => Number(h.panorama_id) === Number(editPanorama.id));
    const draftLpHotspot = (!editingHotspotId && editPanorama && nuevoHotspot.imagen && (nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona'))
      ? [{
          id: 'draft-lp',
          _isDraft: true,
          panorama_id: editPanorama.id,
          pitch: Number(nuevoHotspot.pitch) || 0,
          yaw: Number(nuevoHotspot.yaw) || 0,
          css_class: nuevoHotspot.css_class,
          imagen: nuevoHotspot.imagen,
          transform_json: JSON.stringify(
            nuevoHotspot.css_class === 'hotspot-persona'
              ? normalizePersonaTransformFromDb(nuevoHotspot.flecha_transform || {})
              : { ...DEFAULT_FLECHA_TRANSFORM, scaleY: 1, ...(nuevoHotspot.flecha_transform || {}) }
          ),
          ancho: nuevoHotspot.ancho,
          alto: nuevoHotspot.alto
        }]
      : [];
    const listToRender = [...hotspotsActuales, ...draftLpHotspot];
    listToRender.forEach(h => {
      const isDraftRow = h._isDraft === true;
      const isEditingRow = isDraftRow || (editingHotspotId != null && Number(editingHotspotId) === Number(h.id));
      const spotPannellumId = isDraftRow ? 'draft-lp' : 'exist-' + h.id;
      const hotspotClassId = `hotspot-id-${h.id}`;
      let cssClassRaw = cssBase(h);
      if (isEditingRow) {
        cssClassRaw = buildHotspotCssClass(nuevoHotspot);
      }
      const cssClass = `${cssClassRaw} hotspot-constructor-existente ${hotspotClassId}`
        + (cssClassRaw.includes('hotspot-flecha-pulse') ? ' hotspot-flecha-suelo' : '')
        + (cssClassRaw.includes('hotspot-persona') ? ' hotspot-mas-fijo' : '');
      const isFlecha = cssClass.includes('hotspot-flecha-pulse');
      const isBurbuja = cssClass.includes('hotspot-burbuja');
      const isLogo = cssClass.includes('hotspot-caja-logo');
      const isPersona = cssClass.includes('hotspot-persona');
      const isValla = cssClass.includes('hotspot-valla');
      const spot = {
        id: spotPannellumId,
        pitch: parseFloat(h.pitch) || 0,
        yaw: parseFloat(h.yaw) || 0,
        type: 'info',
        text: h.nombre || h.titulo || (isFlecha ? 'Siguiente' : (isBurbuja ? 'Burbuja' : (isValla ? 'Valla' : 'Enlace'))),
        cssClass
      };
      spot.createTooltipFunc = (div) => {
        if (!div) return;
        div.style.setProperty('visibility', 'visible', 'important');
        div.style.setProperty('opacity', '1', 'important');
        div.style.setProperty('pointer-events', 'auto', 'important');
        if (isFlecha) {
          div.style.setProperty('display', 'block', 'important');
          div.style.setProperty('overflow', 'visible', 'important');
          applyCssClassesToHotspotChain(div, cssClass);
          const savedTransform = parseTransform(h.transform_json);
          const transformToApply = isEditingRow ? (nuevoHotspot.flecha_transform || savedTransform) : savedTransform;
          applyTransformVarsToHotspotChain(div, transformToApply);
          if (isEditingRow) {
            const ensureHandle = (parent, role) => {
              let handle = parent.querySelector(`[data-handle="${role}"]`);
              if (!handle) {
                handle = document.createElement('span');
                handle.className = `flecha-handle flecha-handle-${role}`;
                handle.setAttribute('data-handle', role);
                parent.appendChild(handle);
              }
              return handle;
            };
            let box = div.querySelector('.flecha-transform-box');
            if (!box) {
              box = document.createElement('div');
              box.className = 'flecha-transform-box';
              div.appendChild(box);
            }
            const bindDrag = (handle, role) => {
              if (!handle || handle.dataset.bound === '1') return;
              handle.dataset.bound = '1';
              handle.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); });
              handle.addEventListener('mousedown', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                transformingRef.current = true;
                const startX = ev.clientX;
                const startY = ev.clientY;
                const base = { ...DEFAULT_FLECHA_TRANSFORM, ...(nuevoHotspot.flecha_transform || savedTransform) };
                let latest = { ...base };
                let latestPitch = Number(nuevoHotspot.pitch) || 0;
                let latestYaw = Number(nuevoHotspot.yaw) || 0;
                const onMove = (mv) => {
                  if (mv.cancelable) mv.preventDefault();
                  const dx = mv.clientX - startX;
                  const dy = mv.clientY - startY;
                  if (role === 'rotate') latest.rotateDelta = base.rotateDelta + dx * 0.6;
                  if (role === 'scale') {
                    const factor = clamp(1 + ((Math.abs(dx) > Math.abs(dy) ? dx : -dy) / 180), 0.5, 2.2);
                    latest.scaleX = clamp(base.scaleX * factor, 0.4, 2.6);
                    latest.scaleY = clamp(base.scaleY * factor, 0.05, 1.6);
                  }
                  if (role === 'scale-x') latest.scaleX = clamp(base.scaleX + dx / 140, 0.4, 2.8);
                  if (role === 'scale-y') latest.scaleY = clamp(base.scaleY - dy / 120, 0.05, 1.8);
                  if (role === 'skew') {
                    latest.skewX = clamp(base.skewX + dx * 0.35, -40, 40);
                    latest.skewY = clamp((base.skewY || 0) - dy * 0.25, -30, 30);
                  }
                  if (role === 'move') {
                    latest.offsetX = clamp(base.offsetX + dx, -60, 60);
                    latest.offsetY = clamp(base.offsetY + dy, -60, 60);
                    try {
                      const coords = pannellumRef.current?.mouseEventToCoords?.(mv);
                      if (Array.isArray(coords) && coords.length >= 2) {
                        latestPitch = Number(coords[0]) || 0;
                        latestYaw = Number(coords[1]) || 0;
                        lastClickCoordsRef.current = { pitch: latestPitch, yaw: latestYaw };
                        const cfg = pannellumRef.current?.getConfig?.();
                        const ph = cfg?.hotSpots?.find(s => String(s?.id) === spotPannellumId);
                        if (ph) { ph.pitch = latestPitch; ph.yaw = latestYaw; pannellumRef.current?.resize?.(); }
                      }
                    } catch (_) {}
                  }
                  if (role === 'persp-tl') {
                    latest.perspectiveX = clamp((base.perspectiveX || 0) - dx * 0.35, -30, 30);
                    latest.perspectiveY = clamp((base.perspectiveY || 0) - dy * 0.35, -30, 30);
                  }
                  if (role === 'persp-tr') {
                    latest.perspectiveX = clamp((base.perspectiveX || 0) + dx * 0.35, -30, 30);
                    latest.perspectiveY = clamp((base.perspectiveY || 0) - dy * 0.35, -30, 30);
                  }
                  if (role === 'persp-bl') {
                    latest.perspectiveX = clamp((base.perspectiveX || 0) - dx * 0.35, -30, 30);
                    latest.perspectiveY = clamp((base.perspectiveY || 0) + dy * 0.35, -30, 30);
                  }
                  if (role === 'persp-br') {
                    latest.perspectiveX = clamp((base.perspectiveX || 0) + dx * 0.35, -30, 30);
                    latest.perspectiveY = clamp((base.perspectiveY || 0) + dy * 0.35, -30, 30);
                  }
                  applyTransformVarsToHotspotChain(div, latest);
                  setNuevoHotspot(prev => ({ ...prev, pitch: latestPitch, yaw: latestYaw, flecha_transform: latest }));
                };
                const onUp = () => {
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                  setNuevoHotspot(prev => ({ ...prev, pitch: latestPitch, yaw: latestYaw, flecha_transform: latest }));
                  setTimeout(() => { transformingRef.current = false; }, 0);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              });
            };
            bindDrag(box, 'move');
            bindDrag(div, 'move');
            bindDrag(ensureHandle(box, 'nw'), 'scale');
            bindDrag(ensureHandle(box, 'ne'), 'scale');
            bindDrag(ensureHandle(box, 'sw'), 'scale');
            bindDrag(ensureHandle(box, 'se'), 'scale');
            bindDrag(ensureHandle(box, 'n'), 'scale-y');
            bindDrag(ensureHandle(box, 's'), 'scale-y');
            bindDrag(ensureHandle(box, 'e'), 'scale-x');
            bindDrag(ensureHandle(box, 'w'), 'scale-x');
            bindDrag(ensureHandle(box, 'rotate'), 'rotate');
            bindDrag(ensureHandle(box, 'skew-left'), 'skew');
            bindDrag(ensureHandle(box, 'skew-right'), 'skew');
            bindDrag(ensureHandle(box, 'skew-tl'), 'skew');
            bindDrag(ensureHandle(box, 'skew-tr'), 'skew');
            bindDrag(ensureHandle(box, 'skew-bl'), 'skew');
            bindDrag(ensureHandle(box, 'skew-br'), 'skew');
            bindDrag(ensureHandle(box, 'persp-tl'), 'persp-tl');
            bindDrag(ensureHandle(box, 'persp-tr'), 'persp-tr');
            bindDrag(ensureHandle(box, 'persp-bl'), 'persp-bl');
            bindDrag(ensureHandle(box, 'persp-br'), 'persp-br');
          }
        } else if (isValla && h.imagen) {
          const savedTransform = parseTransform(h.transform_json);
          const transformToApply = isEditingRow ? (nuevoHotspot.flecha_transform || savedTransform) : savedTransform;
          applyTransformVarsToHotspotChain(div, transformToApply);
          const ancho = isEditingRow ? (Number(nuevoHotspot.ancho) || 300) : (Number(h.ancho) || 300);
          const alto = isEditingRow ? (Number(nuevoHotspot.alto) || 200) : (Number(h.alto) || 200);
          const totalAltura = alto + 578;
          div.style.setProperty('width', `${ancho}px`, 'important');
          div.style.setProperty('height', `${totalAltura}px`, 'important');
          div.style.setProperty('min-width', `${ancho}px`, 'important');
          div.style.setProperty('max-width', `${ancho}px`, 'important');
          div.style.setProperty('min-height', `${totalAltura}px`, 'important');
          div.style.setProperty('max-height', `${totalAltura}px`, 'important');
          div.style.setProperty('margin', `-${totalAltura / 2}px 0 0 -${ancho / 2}px`, 'important');
          div.style.setProperty('overflow', 'visible', 'important');
          div.style.setProperty('display', 'block', 'important');
          div.style.setProperty('border', '2px solid rgba(13,110,253,0.6)', 'important');
          div.style.setProperty('transform-origin', 'center center', 'important');
          const mediaUrl = buildImageUrl(h.imagen);
          if (isEditingRow) {
            const ensureHandle = (parent, role) => {
              let handle = parent.querySelector(`[data-handle="${role}"]`);
              if (!handle) {
                handle = document.createElement('span');
                handle.className = `flecha-handle flecha-handle-${role}`;
                handle.setAttribute('data-handle', role);
                parent.appendChild(handle);
              }
              return handle;
            };
            let box = div.querySelector('.flecha-transform-box');
            if (!box) {
              box = document.createElement('div');
              box.className = 'flecha-transform-box valla-edit-box';
              div.appendChild(box);
            }
            box.style.setProperty('pointer-events', 'auto', 'important');
            box.style.setProperty('z-index', '10', 'important');
            box.style.setProperty('cursor', 'move', 'important');
            const bindDrag = (handle, role) => {
              if (!handle || handle.dataset.bound === '1') return;
              handle.dataset.bound = '1';
              handle.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); });
              handle.addEventListener('mousedown', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                transformingRef.current = true;
                const startX = ev.clientX;
                const startY = ev.clientY;
                const base = { ...DEFAULT_FLECHA_TRANSFORM, ...(nuevoHotspot.flecha_transform || savedTransform) };
                let latest = { ...base };
                let latestPitch = Number(nuevoHotspot.pitch) || 0;
                let latestYaw = Number(nuevoHotspot.yaw) || 0;
                const onMove = (mv) => {
                  if (mv.cancelable) mv.preventDefault();
                  const dx = mv.clientX - startX;
                  const dy = mv.clientY - startY;
                  if (role === 'rotate') latest.rotateDelta = base.rotateDelta + dx * 0.6;
                  if (role === 'scale') {
                    const factor = clamp(1 + ((Math.abs(dx) > Math.abs(dy) ? dx : -dy) / 180), 0.5, 2.2);
                    latest.scaleX = clamp(base.scaleX * factor, 0.4, 2.6);
                    latest.scaleY = clamp(base.scaleY * factor, 0.3, 2);
                  }
                  if (role === 'scale-x') latest.scaleX = clamp(base.scaleX + dx / 140, 0.4, 2.8);
                  if (role === 'scale-y') latest.scaleY = clamp(base.scaleY - dy / 120, 0.3, 2);
                  if (role === 'skew') { latest.skewX = clamp((base.skewX || 0) + dx * 0.15, -25, 25); latest.skewY = clamp((base.skewY || 0) - dy * 0.12, -25, 25); }
                  if (role === 'move') {
                    latest.offsetX = clamp(base.offsetX + dx, -80, 80);
                    latest.offsetY = clamp(base.offsetY + dy, -80, 80);
                    try {
                      const coords = pannellumRef.current?.mouseEventToCoords?.(mv);
                      if (Array.isArray(coords) && coords.length >= 2) {
                        latestPitch = Number(coords[0]) || 0;
                        latestYaw = Number(coords[1]) || 0;
                        lastClickCoordsRef.current = { pitch: latestPitch, yaw: latestYaw };
                        const cfg = pannellumRef.current?.getConfig?.();
                        const ph = cfg?.hotSpots?.find(s => String(s?.id) === spotPannellumId);
                        if (ph) { ph.pitch = latestPitch; ph.yaw = latestYaw; pannellumRef.current?.resize?.(); }
                      }
                    } catch (_) {}
                  }
                  if (role === 'persp-tl') { latest.perspectiveX = clamp((base.perspectiveX || 0) - dx * 0.35, -30, 30); latest.perspectiveY = clamp((base.perspectiveY || 0) - dy * 0.35, -30, 30); }
                  if (role === 'persp-tr') { latest.perspectiveX = clamp((base.perspectiveX || 0) + dx * 0.35, -30, 30); latest.perspectiveY = clamp((base.perspectiveY || 0) - dy * 0.35, -30, 30); }
                  if (role === 'persp-bl') { latest.perspectiveX = clamp((base.perspectiveX || 0) - dx * 0.35, -30, 30); latest.perspectiveY = clamp((base.perspectiveY || 0) + dy * 0.35, -30, 30); }
                  if (role === 'persp-br') { latest.perspectiveX = clamp((base.perspectiveX || 0) + dx * 0.35, -30, 30); latest.perspectiveY = clamp((base.perspectiveY || 0) + dy * 0.35, -30, 30); }
                  applyTransformVarsToHotspotChain(div, latest);
                  setNuevoHotspot(prev => ({ ...prev, pitch: latestPitch, yaw: latestYaw, flecha_transform: latest }));
                };
                const onUp = () => {
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                  setNuevoHotspot(prev => ({ ...prev, pitch: latestPitch, yaw: latestYaw, flecha_transform: latest }));
                  setTimeout(() => { transformingRef.current = false; }, 0);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              });
            };
            bindDrag(box, 'move');
            bindDrag(div, 'move');
            bindDrag(ensureHandle(box, 'nw'), 'scale');
            bindDrag(ensureHandle(box, 'ne'), 'scale');
            bindDrag(ensureHandle(box, 'sw'), 'scale');
            bindDrag(ensureHandle(box, 'se'), 'scale');
            bindDrag(ensureHandle(box, 'n'), 'scale-y');
            bindDrag(ensureHandle(box, 's'), 'scale-y');
            bindDrag(ensureHandle(box, 'e'), 'scale-x');
            bindDrag(ensureHandle(box, 'w'), 'scale-x');
            bindDrag(ensureHandle(box, 'rotate'), 'rotate');
            bindDrag(ensureHandle(box, 'skew-left'), 'skew');
            bindDrag(ensureHandle(box, 'skew-right'), 'skew');
          }
          if (!div.querySelector('.hotspot-valla-panel')) {
            const contenido = document.createElement('div');
            contenido.className = 'hotspot-valla-contenido';
            if (isEditingRow) {
              contenido.style.setProperty('pointer-events', 'none', 'important');
            }
            const marco = document.createElement('div');
            marco.className = 'hotspot-valla-marco';
            marco.style.setProperty('pointer-events', 'none', 'important');
            marco.style.cssText = 'background:#1a1a1a;padding:8px;border-radius:6px 6px 12px 12px;';
            const panel = document.createElement('div');
            panel.className = 'hotspot-valla-panel';
            panel.style.cssText = `width:${ancho}px;height:${alto}px;overflow:hidden;border-radius:2px;background:#fff;`;
            const img = document.createElement('img');
            img.src = mediaUrl;
            img.alt = h.nombre || h.titulo || 'Marquesina';
            img.className = 'hotspot-valla-img';
            img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
            img.onerror = () => { panel.innerHTML = '<span style="color:#333;font-size:10px;">Marquesina</span>'; };
            panel.appendChild(img);
            marco.appendChild(panel);
            contenido.appendChild(marco);
            const pilar = document.createElement('div');
            pilar.className = 'hotspot-valla-pilar';
            pilar.style.cssText = 'flex:0 0 auto;width:20px;min-width:20px;max-width:20px;height:550px;background:#1a1a1a;margin:-2px auto 0;pointer-events:none;';
            contenido.appendChild(pilar);
            const base = document.createElement('div');
            base.className = 'hotspot-valla-base';
            base.style.cssText = 'flex:0 0 auto;width:34px;height:14px;background:#0d0d0d;margin:-2px auto 0;border-radius:2px;pointer-events:none;';
            contenido.appendChild(base);
            div.appendChild(contenido);
          }
        } else if (isBurbuja && h.panorama_destino_id) {
          const dest = panoramas.find(p => Number(p.id) === Number(h.panorama_destino_id));
          const destUrl = dest?.ruta_archivo ? (dest.ruta_archivo.startsWith('http') ? dest.ruta_archivo : (API_BASE + (dest.ruta_archivo.startsWith('/') ? '' : '/') + dest.ruta_archivo)) : '';
          const textoBurbuja =
            (h.titulo && String(h.titulo).trim()) ||
            (h.nombre && String(h.nombre).trim()) ||
            (dest?.nombre && String(dest.nombre).trim()) ||
            'Siguiente escena';
          div.style.setProperty('display', 'flex', 'important');
          div.style.setProperty('alignItems', 'center', 'important');
          div.style.setProperty('justifyContent', 'center', 'important');
          div.style.setProperty('width', '90px', 'important');
          div.style.setProperty('height', '90px', 'important');
          div.style.setProperty('overflow', 'visible', 'important');
          div.style.setProperty('margin', '0', 'important');
          div.style.setProperty('pointer-events', 'auto', 'important');
          div.style.setProperty('border', '2px solid rgba(13,110,253,0.6)', 'important');
          div.setAttribute('title', textoBurbuja);
          if (!div.querySelector('.hotspot-burbuja-wrap')) {
            div.innerHTML = '';
            const wrap = document.createElement('div');
            wrap.className = 'hotspot-burbuja-wrap';
            wrap.style.cssText = 'position:relative;width:100%;height:100%;overflow:visible;';
            const inner = document.createElement('div');
            inner.className = 'hotspot-burbuja-inner';
            inner.style.cssText = 'width:100%;height:100%;border-radius:50%;overflow:hidden;background:#333;border:3px solid rgba(255,255,255,0.9);box-shadow:inset 0 0 20px rgba(0,0,0,0.5);';
            if (destUrl) {
              const img = document.createElement('img');
              img.src = destUrl;
              img.alt = textoBurbuja;
              img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:center center;display:block;';
              img.onerror = () => { inner.innerHTML = '<span style="color:#fff;font-size:10px;display:flex;align-items:center;justify-content:center;height:100%;">Preview</span>'; };
              inner.appendChild(img);
            } else {
              inner.innerHTML = '<span style="color:#fff;font-size:10px;display:flex;align-items:center;justify-content:center;height:100%;">Destino</span>';
            }
            const cap = document.createElement('span');
            cap.className = 'hotspot-burbuja-caption constructor-burbuja-caption';
            cap.textContent = textoBurbuja;
            cap.style.cssText =
              'position:absolute;left:50%;top:100%;transform:translateX(-50%);margin-top:5px;max-width:180px;text-align:center;font-size:11px;line-height:1.25;color:#fff;font-weight:600;text-shadow:0 1px 3px rgba(0,0,0,0.9);pointer-events:none;z-index:2;padding:0 4px;';
            wrap.appendChild(inner);
            wrap.appendChild(cap);
            div.appendChild(wrap);
          }
        } else if ((isLogo || isPersona) && h.imagen) {
          const savedTransformLogo = parseTransform(h.transform_json);
          const rawT = isEditingRow ? (nuevoHotspot.flecha_transform || savedTransformLogo) : savedTransformLogo;
          const transformToApplyLogo = isPersona ? normalizePersonaTransformFromDb(rawT) : rawT;
          applyTransformVarsToHotspotChain(
            div,
            isLogo ? { ...transformToApplyLogo, scaleY: transformToApplyLogo.scaleY ?? 1 } : transformToApplyLogo
          );
          const { w: boxW, h: boxH } = clampedLogoPersonaSize(isLogo, isEditingRow, h);
          div.style.setProperty('width', `${boxW}px`, 'important');
          div.style.setProperty('height', `${boxH}px`, 'important');
          div.style.setProperty('margin', '0', 'important');
          div.style.setProperty('overflow', 'visible', 'important');
          div.style.setProperty('display', 'flex', 'important');
          div.style.setProperty('alignItems', isPersona ? 'flex-end' : 'center', 'important');
          div.style.setProperty('justifyContent', 'center', 'important');
          div.style.setProperty('transform-origin', isPersona ? 'center bottom' : 'center center', 'important');
          if (isLogo) {
            div.style.setProperty('pointer-events', 'none', 'important');
          } else if (isPersona && isEditingRow) {
            div.style.setProperty('pointer-events', 'none', 'important');
          }
          if (isEditingRow) {
            if (isLogo) {
              div.style.setProperty('border', '2px solid rgba(140, 150, 165, 0.45)', 'important');
              div.style.setProperty('box-shadow', 'none', 'important');
            } else {
              div.style.setProperty('border', '2px solid rgba(13,110,253,0.6)', 'important');
            }
          }
          const mediaUrl = buildImageUrl(h.imagen);
          const esVideo = isPersona && isVideoAsset(h.imagen);
          if (isLogo && !div.querySelector('.logo-stand-shell')) {
            div.innerHTML = '';
            const shell = document.createElement('div');
            shell.className = 'logo-stand-shell';
            const bgImg = document.createElement('img');
            bgImg.className = 'logo-stand-bg-img';
            bgImg.src = standBackgroundUrl;
            bgImg.alt = '';
            bgImg.setAttribute('aria-hidden', 'true');
            bgImg.draggable = false;
            const fascia = document.createElement('div');
            fascia.className = 'logo-stand-fascia';
            const contenido = document.createElement('div');
            contenido.className = 'logo-persona-contenido logo-relleno';
            const img = document.createElement('img');
            img.src = mediaUrl;
            img.alt = h.nombre || h.titulo || '';
            img.className = 'hotspot-caja-img';
            img.style.cssText = 'width:100%;height:100%;object-fit:contain;';
            img.onerror = () => { contenido.innerHTML = '<span style="color:#666;font-size:10px;">Logo</span>'; };
            contenido.appendChild(img);
            fascia.appendChild(contenido);
            const base = document.createElement('div');
            base.className = 'logo-stand-base';
            base.setAttribute('aria-hidden', 'true');
            shell.appendChild(bgImg);
            shell.appendChild(fascia);
            shell.appendChild(base);
            div.appendChild(shell);
          } else if (!isLogo && !div.querySelector('.logo-persona-contenido')) {
            div.innerHTML = '';
            const contenido = document.createElement('div');
            contenido.className = 'logo-persona-contenido';
            contenido.style.cssText = 'position:relative;width:100%;height:100%;margin:0;box-sizing:border-box;border-radius:4px;overflow:hidden;background:transparent;display:flex;align-items:flex-end;justify-content:center;transform-origin:center bottom;';
            if (esVideo) {
              const video = document.createElement('video');
              video.src = mediaUrl;
              video.autoplay = true;
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
              video.style.cssText = 'width:100%;height:100%;object-fit:contain;object-position:center bottom;';
              video.onerror = () => { contenido.innerHTML = '<span style="color:#fff;font-size:10px;">Video</span>'; };
              contenido.appendChild(video);
            } else {
              const img = document.createElement('img');
              img.src = mediaUrl;
              img.alt = h.nombre || h.titulo || '';
              img.style.cssText = 'width:100%;height:100%;object-fit:contain;object-position:center bottom;';
              img.onerror = () => { contenido.innerHTML = '<span style="color:#fff;font-size:10px;">Logo</span>'; };
              contenido.appendChild(img);
            }
            div.appendChild(contenido);
          }
          if (isLogo) {
            const shellEl = div.querySelector('.logo-stand-shell');
            if (shellEl) {
              shellEl.style.setProperty('pointer-events', 'none', 'important');
              const fasciaEl = shellEl.querySelector('.logo-stand-fascia');
              if (fasciaEl) {
                fasciaEl.style.setProperty('pointer-events', 'auto', 'important');
                fasciaEl.style.setProperty('cursor', 'pointer', 'important');
              }
              const imgEl = fasciaEl?.querySelector('.hotspot-caja-img');
              if (imgEl) imgEl.style.setProperty('pointer-events', 'auto', 'important');
            }
          }
          if (isPersona && isEditingRow) {
            const contPersona = div.querySelector('.logo-persona-contenido');
            if (contPersona) contPersona.style.setProperty('pointer-events', 'auto', 'important');
          }
          if (isEditingRow && (isLogo || isPersona)) {
            const ensureHandle = (parent, role) => {
              let handle = parent.querySelector(`[data-handle="${role}"]`);
              if (!handle) {
                handle = document.createElement('span');
                handle.className = `flecha-handle flecha-handle-${role}`;
                handle.setAttribute('data-handle', role);
                parent.appendChild(handle);
              }
              return handle;
            };
            let box = div.querySelector('.flecha-transform-box.logo-edit-box');
            if (!box) {
              box = document.createElement('div');
              box.className = 'flecha-transform-box logo-edit-box';
              div.appendChild(box);
            }
            box.style.setProperty('pointer-events', 'auto', 'important');
            box.style.setProperty('z-index', '10', 'important');
            const bindDrag = (handle, role) => {
              if (!handle || handle.dataset.bound === '1') return;
              handle.dataset.bound = '1';
              handle.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); });
              handle.addEventListener('mousedown', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                transformingRef.current = true;
                const startX = ev.clientX;
                const startY = ev.clientY;
                const base = isPersona
                  ? { ...DEFAULT_PERSONA_TRANSFORM, ...(nuevoHotspot.flecha_transform || savedTransformLogo) }
                  : { ...DEFAULT_FLECHA_TRANSFORM, scaleY: 1, ...(nuevoHotspot.flecha_transform || savedTransformLogo) };
                let latest = { ...base };
                let latestPitch = Number(nuevoHotspot.pitch) || 0;
                let latestYaw = Number(nuevoHotspot.yaw) || 0;
                const logoLikeLimits = isLogo || isPersona;
                const onMove = (mv) => {
                  if (mv.cancelable) mv.preventDefault();
                  const dx = mv.clientX - startX;
                  const dy = mv.clientY - startY;
                  const scaleMin = logoLikeLimits ? 0.15 : 0.3;
                  const scaleMax = logoLikeLimits ? 4 : 2.8;
                  if (role === 'rotate') latest.rotateDelta = base.rotateDelta + dx * 0.6;
                  if (role === 'scale') {
                    const factor = clamp(1 + ((Math.abs(dx) > Math.abs(dy) ? dx : -dy) / 180), 0.5, 2.2);
                    latest.scaleX = clamp(base.scaleX * factor, scaleMin, scaleMax);
                    latest.scaleY = clamp((base.scaleY ?? 1) * factor, scaleMin, scaleMax);
                  }
                  if (role === 'scale-x') latest.scaleX = clamp(base.scaleX + dx / 140, scaleMin, scaleMax);
                  if (role === 'scale-y') latest.scaleY = clamp((base.scaleY ?? 1) + dy / 120, scaleMin, scaleMax);
                  const offsetLimit = logoLikeLimits ? 150 : 60;
                  const skewLimit = logoLikeLimits ? 45 : 25;
                  const perspLim = logoLikeLimits ? 55 : 30;
                  const perspSens = logoLikeLimits ? 0.52 : 0.35;
                  if (role === 'skew') { latest.skewX = clamp((base.skewX || 0) + dx * 0.2, -skewLimit, skewLimit); latest.skewY = clamp((base.skewY || 0) - dy * 0.15, -skewLimit, skewLimit); }
                  if (role === 'move') {
                    latest.offsetX = clamp(base.offsetX + dx, -offsetLimit, offsetLimit);
                    latest.offsetY = clamp(base.offsetY + dy, -offsetLimit, offsetLimit);
                    try {
                      const coords = pannellumRef.current?.mouseEventToCoords?.(mv);
                      if (Array.isArray(coords) && coords.length >= 2) {
                        latestPitch = Number(coords[0]) || 0;
                        latestYaw = Number(coords[1]) || 0;
                        lastClickCoordsRef.current = { pitch: latestPitch, yaw: latestYaw };
                        const cfg = pannellumRef.current?.getConfig?.();
                        const ph = cfg?.hotSpots?.find(s => String(s?.id) === spotPannellumId);
                        if (ph) { ph.pitch = latestPitch; ph.yaw = latestYaw; pannellumRef.current?.resize?.(); }
                      }
                    } catch (_) {}
                  }
                  if (role === 'persp-tl') { latest.perspectiveX = clamp((base.perspectiveX || 0) - dx * perspSens, -perspLim, perspLim); latest.perspectiveY = clamp((base.perspectiveY || 0) - dy * perspSens, -perspLim, perspLim); }
                  if (role === 'persp-tr') { latest.perspectiveX = clamp((base.perspectiveX || 0) + dx * perspSens, -perspLim, perspLim); latest.perspectiveY = clamp((base.perspectiveY || 0) - dy * perspSens, -perspLim, perspLim); }
                  if (role === 'persp-bl') { latest.perspectiveX = clamp((base.perspectiveX || 0) - dx * perspSens, -perspLim, perspLim); latest.perspectiveY = clamp((base.perspectiveY || 0) + dy * perspSens, -perspLim, perspLim); }
                  if (role === 'persp-br') { latest.perspectiveX = clamp((base.perspectiveX || 0) + dx * perspSens, -perspLim, perspLim); latest.perspectiveY = clamp((base.perspectiveY || 0) + dy * perspSens, -perspLim, perspLim); }
                  applyTransformVarsToHotspotChain(div, latest);
                  setNuevoHotspot(prev => ({ ...prev, pitch: latestPitch, yaw: latestYaw, flecha_transform: latest }));
                };
                const onUp = () => {
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                  setNuevoHotspot(prev => ({ ...prev, pitch: latestPitch, yaw: latestYaw, flecha_transform: latest }));
                  setTimeout(() => { transformingRef.current = false; }, 0);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              });
            };
            bindDrag(box, 'move');
            bindDrag(div, 'move');
            bindDrag(ensureHandle(box, 'nw'), 'scale');
            bindDrag(ensureHandle(box, 'ne'), 'scale');
            bindDrag(ensureHandle(box, 'sw'), 'scale');
            bindDrag(ensureHandle(box, 'se'), 'scale');
            bindDrag(ensureHandle(box, 'n'), 'scale-y');
            bindDrag(ensureHandle(box, 's'), 'scale-y');
            bindDrag(ensureHandle(box, 'e'), 'scale-x');
            bindDrag(ensureHandle(box, 'w'), 'scale-x');
            bindDrag(ensureHandle(box, 'rotate'), 'rotate');
            bindDrag(ensureHandle(box, 'skew-left'), 'skew');
            bindDrag(ensureHandle(box, 'skew-right'), 'skew');
            bindDrag(ensureHandle(box, 'persp-tl'), 'persp-tl');
            bindDrag(ensureHandle(box, 'persp-tr'), 'persp-tr');
            bindDrag(ensureHandle(box, 'persp-bl'), 'persp-bl');
            bindDrag(ensureHandle(box, 'persp-br'), 'persp-br');
          }
        }
        // Solo tocar contenedores de Pannellum del hotspot, no el contenedor principal del visor
        let p = div.parentElement;
        let d = 0;
        while (p && d < 5) {
          if (p.classList && (p.classList.contains('pnlm-hotspot-base') || p.classList.contains('pnlm-hotspot'))) {
            p.style.setProperty('visibility', 'visible', 'important');
            p.style.setProperty('opacity', '1', 'important');
            if ((isLogo || isPersona) && h.imagen) {
              const { w: pw, h: ph } = clampedLogoPersonaSize(isLogo, isEditingRow, h);
              if (isLogo || isPersona) {
                p.style.setProperty('width', `${pw}px`, 'important');
                p.style.setProperty('height', `${ph}px`, 'important');
                p.style.setProperty('min-width', `${pw}px`, 'important');
                p.style.setProperty('min-height', `${ph}px`, 'important');
                p.style.setProperty('margin', '0', 'important');
                p.style.setProperty('display', 'flex', 'important');
                p.style.setProperty('align-items', isLogo ? 'center' : 'flex-end', 'important');
                p.style.setProperty('justify-content', 'center', 'important');
              }
            }
          }
          p = p.parentElement;
          d++;
        }
      };
      hotSpotsList.push(spot);
    });
    // Vista inicial: si estamos editando un hotspot, centrar en su pitch/yaw (no en el promedio ni recrear sin mirar al marcador)
    const maxHfov = 120;
    let initialPitch = 0;
    let initialYaw = 0;
    let initialHfov = maxHfov;
    if (editingHotspotId != null) {
      initialPitch = Number(nuevoHotspot.pitch) || 0;
      initialYaw = Number(nuevoHotspot.yaw) || 0;
      initialHfov = Math.min(maxHfov, Math.max(50, Number(entradaVista.hfov) || 95));
    } else {
      const hotspotsParaVista = hotspotsActuales.filter(h => {
        const css = String(h.css_class || '').toLowerCase();
        const tipo = String(h.tipo || '').toLowerCase();
        return !css.includes('hotspot-valla') && tipo !== 'valla';
      });
      const refHotspots = hotspotsParaVista.length > 0 ? hotspotsParaVista : hotspotsActuales;
      if (refHotspots.length > 0) {
        const sumPitch = refHotspots.reduce((s, h) => s + (Number(h.pitch) || 0), 0);
        const sumYaw = refHotspots.reduce((s, h) => s + (Number(h.yaw) || 0), 0);
        initialPitch = sumPitch / refHotspots.length;
        initialYaw = sumYaw / refHotspots.length;
      }
    }
    try {
      const viewer = window.pannellum.viewer(viewerRef.current, {
        type: 'equirectangular',
        panorama: url,
        autoLoad: true,
        showControls: false,
        pitch: initialPitch,
        yaw: initialYaw,
        hfov: initialHfov,
        minHfov: 50,
        maxHfov,
        hotSpots: hotSpotsList
      });
      pannellumRef.current = viewer;

      if (typeof viewer.on === 'function') {
        viewer.on('load', () => {
          setViewerLoadedTrigger(t => t + 1);
          if (editingHotspotId != null) {
            const p = Number(nuevoHotspot.pitch) || 0;
            const y = Number(nuevoHotspot.yaw) || 0;
            const hf = Math.min(maxHfov, Math.max(50, Number(entradaVista.hfov) || 95));
            try {
              if (typeof viewer.lookAt === 'function') viewer.lookAt(p, y, hf, 0);
            } catch (_) {}
          }
          /* Forzar recálculo de posición con dimensiones reales del DOM */
          setTimeout(() => {
            try {
              if (typeof viewer.resize === 'function') viewer.resize();
              if (typeof viewer.getPitch === 'function') {
                viewer.lookAt(viewer.getPitch(), viewer.getYaw(), viewer.getHfov(), 0);
              }
            } catch (_) {}
          }, 150);
        });
      }
    } catch (err) {
      console.error('Error Pannellum:', err);
    }
    return () => {
      try { pannellumRef.current?.destroy(); } catch (e) {}
      pannellumRef.current = null;
    };
  }, [pannellumReady, editPanorama, editPanorama?.id, panoramas, hotspots, editingHotspotId, nuevoHotspot.ancho, nuevoHotspot.alto, draftLpFingerprint]);

  // Aplicar transformación en vivo sin recrear el viewer (hotspot en edición o borrador logo/persona)
  useEffect(() => {
    if (!viewerRef.current) return;
    const root = viewerRef.current;
    if (editingHotspotId != null) {
      const nodes = root.querySelectorAll(`.hotspot-id-${editingHotspotId}`);
      nodes.forEach((el) => {
        if (!el || !el.classList) return;
        const isPers = el.classList.contains('hotspot-persona');
        const t = isPers
          ? { ...DEFAULT_PERSONA_TRANSFORM, ...(nuevoHotspot.flecha_transform || {}) }
          : { ...DEFAULT_FLECHA_TRANSFORM, ...(nuevoHotspot.flecha_transform || {}) };
        const defaultScaleY = (el.classList.contains('hotspot-caja-logo') || isPers) ? 1 : 0.62;
        applyFlechaCssVarsToElements([el], t, defaultScaleY);
      });
    }
    const draftLive =
      !editingHotspotId && nuevoHotspot.imagen && (nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona');
    if (draftLive) {
      const nodes = root.querySelectorAll('.hotspot-id-draft-lp');
      const t =
        nuevoHotspot.css_class === 'hotspot-persona'
          ? { ...DEFAULT_PERSONA_TRANSFORM, ...(nuevoHotspot.flecha_transform || {}) }
          : { ...DEFAULT_FLECHA_TRANSFORM, scaleY: 1, ...(nuevoHotspot.flecha_transform || {}) };
      applyFlechaCssVarsToElements(nodes, t, 1);
    }
  }, [editingHotspotId, nuevoHotspot.flecha_transform, nuevoHotspot.css_class, nuevoHotspot.imagen]);

  useEffect(() => {
    const v = pannellumRef.current;
    if (!v || typeof v.getConfig !== 'function') return;
    const patch = (spotId, pitch, yaw) => {
      try {
        const cfg = v.getConfig();
        const ph = cfg?.hotSpots?.find(s => String(s?.id) === spotId);
        if (ph) {
          ph.pitch = Number(pitch) || 0;
          ph.yaw = Number(yaw) || 0;
          v.resize?.();
        }
      } catch (_) {}
    };
    if (editingHotspotId != null) {
      patch('exist-' + editingHotspotId, nuevoHotspot.pitch, nuevoHotspot.yaw);
    }
    if (!editingHotspotId && nuevoHotspot.imagen && (nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona')) {
      patch('draft-lp', nuevoHotspot.pitch, nuevoHotspot.yaw);
    }
  }, [editingHotspotId, nuevoHotspot.pitch, nuevoHotspot.yaw, nuevoHotspot.imagen, nuevoHotspot.css_class]);

  // Cambiar forma/direccion/color en vivo sin recargar panorama
  useEffect(() => {
    if (!viewerRef.current || !editingHotspotId) return;
    if (nuevoHotspot.css_class !== 'hotspot-flecha-pulse') return;
    const root = viewerRef.current;
    const cssLive = `${buildHotspotCssClass(nuevoHotspot)} hotspot-constructor-existente hotspot-flecha-suelo`;
    const classesToAdd = cssLive.split(/\s+/).filter(Boolean);
    const arrowClassTokens = [
      'hotspot-flecha-ancha', 'hotspot-flecha-compacta',
      'hotspot-dir-derecha', 'hotspot-dir-arriba', 'hotspot-dir-izquierda', 'hotspot-dir-abajo',
      'hotspot-dir-arriba-derecha', 'hotspot-dir-arriba-izquierda', 'hotspot-dir-abajo-derecha', 'hotspot-dir-abajo-izquierda',
      'hotspot-azul', 'hotspot-rojo', 'hotspot-blanco', 'hotspot-negro'
    ];
    const nodes = root.querySelectorAll(`.hotspot-id-${editingHotspotId}`);
    nodes.forEach((el) => {
      arrowClassTokens.forEach(c => el.classList.remove(c));
      classesToAdd.forEach(c => el.classList.add(c));
      let p = el.parentElement;
      let d = 0;
      while (p && d < 6) {
        if (p.classList && (p.classList.contains('pnlm-hotspot-base') || p.classList.contains('pnlm-hotspot'))) {
          arrowClassTokens.forEach(c => p.classList.remove(c));
          classesToAdd.forEach(c => p.classList.add(c));
        }
        p = p.parentElement;
        d++;
      }
    });
  }, [editingHotspotId, nuevoHotspot.css_class, nuevoHotspot.flecha_estilo, nuevoHotspot.flecha_direccion, nuevoHotspot.color]);

  useEffect(() => {
    const v = pannellumRef.current;
    if (!v || typeof v.isLoaded !== 'function' || !v.isLoaded()) return;
    try {
      if (typeof v.removeHotSpot === 'function') v.removeHotSpot('preview');
      const pitch = Number(nuevoHotspot.pitch) || 0;
      const yaw = Number(nuevoHotspot.yaw) || 0;
      const skipPreviewDot =
        (nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona') && nuevoHotspot.imagen;
      if (!skipPreviewDot && (tab === 'pasillos' || tab === 'elementos') && (pitch !== 0 || yaw !== 0)) {
        if (typeof v.addHotSpot === 'function') {
          v.addHotSpot({
            id: 'preview',
            pitch,
            yaw,
            type: 'info',
            text: 'Aquí',
            cssClass: 'hotspot-constructor-preview'
          });
        }
      }
    } catch (e) {}
  }, [viewerLoadedTrigger, tab, nuevoHotspot.pitch, nuevoHotspot.yaw]);

  const hotspotsDePanorama = (panoramaId) =>
    hotspots
      .filter((h) => h.panorama_id === panoramaId)
      .sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0) || a.id - b.id);

  /** Rellena el formulario con los datos de un hotspot para editar */
  const startEditHotspot = (h) => {
    const css = String(h.css_class || 'hotspot-punto').trim();
    const parts = css.split(/\s+/).filter(Boolean);
    let cssBase = 'hotspot-punto';
    if (parts.some(p => p === 'hotspot-flecha-pulse')) cssBase = 'hotspot-flecha-pulse';
    else if (parts.some(p => p === 'hotspot-burbuja')) cssBase = 'hotspot-burbuja';
    else if (parts.some(p => p === 'hotspot-caja-logo')) cssBase = 'hotspot-caja-logo';
    else if (parts.some(p => p === 'hotspot-persona')) cssBase = 'hotspot-persona';
    else if (parts.some(p => p === 'hotspot-valla')) cssBase = 'hotspot-valla';
    let color = '';
    if (parts.includes('hotspot-azul')) color = 'azul';
    else if (parts.includes('hotspot-blanco')) color = 'blanco';
    else if (parts.includes('hotspot-rojo')) color = 'rojo';
    else if (parts.includes('hotspot-negro')) color = 'negro';
    let flecha_estilo = 'simple';
    if (parts.includes('hotspot-flecha-ancha')) flecha_estilo = 'ancha';
    else if (parts.includes('hotspot-flecha-compacta')) flecha_estilo = 'compacta';
    let flecha_direccion = 'derecha';
    if (parts.includes('hotspot-dir-arriba')) flecha_direccion = 'arriba';
    else if (parts.includes('hotspot-dir-izquierda')) flecha_direccion = 'izquierda';
    else if (parts.includes('hotspot-dir-abajo')) flecha_direccion = 'abajo';
    else if (parts.includes('hotspot-dir-arriba-derecha')) flecha_direccion = 'arriba-derecha';
    else if (parts.includes('hotspot-dir-arriba-izquierda')) flecha_direccion = 'arriba-izquierda';
    else if (parts.includes('hotspot-dir-abajo-derecha')) flecha_direccion = 'abajo-derecha';
    else if (parts.includes('hotspot-dir-abajo-izquierda')) flecha_direccion = 'abajo-izquierda';
    let flecha_transform = { ...DEFAULT_FLECHA_TRANSFORM };
    try {
      if (typeof h.transform_json === 'string' && h.transform_json.trim()) {
        const parsed = JSON.parse(h.transform_json);
        flecha_transform = cssBase === 'hotspot-persona'
          ? normalizePersonaTransformFromDb(parsed)
          : { ...DEFAULT_FLECHA_TRANSFORM, ...parsed };
      } else if (cssBase === 'hotspot-persona') {
        flecha_transform = { ...DEFAULT_PERSONA_TRANSFORM };
      }
    } catch (_) {
      if (cssBase === 'hotspot-persona') flecha_transform = { ...DEFAULT_PERSONA_TRANSFORM };
    }
    const pitch = Number(h.pitch);
    const yaw = Number(h.yaw);
    lastClickCoordsRef.current = { pitch: Number.isFinite(pitch) ? pitch : 0, yaw: Number.isFinite(yaw) ? yaw : 0 };
    let galeriaArr = [];
    try {
      if (typeof h.galeria === 'string' && h.galeria.trim()) galeriaArr = JSON.parse(h.galeria);
      else if (Array.isArray(h.galeria)) galeriaArr = h.galeria;
    } catch (_) {}
    setNuevoHotspot({
      pitch: Number.isFinite(pitch) ? pitch : 0,
      yaw: Number.isFinite(yaw) ? yaw : 0,
      css_class: cssBase,
      tieneNavegacion: cssBase === 'hotspot-flecha-pulse' || cssBase === 'hotspot-burbuja',
      panorama_destino_id: h.panorama_destino_id ?? null,
      color,
      flecha_estilo,
      flecha_direccion,
      flecha_transform,
      url_externa: h.url_externa || '',
      titulo: h.titulo || '',
      nombre: h.nombre || '',
      descripcion: h.descripcion || '',
      link: h.link || '',
      link_panorama: h.link_panorama || '',
      telefono: h.telefono || '',
      telefono2: h.telefono2 || '',
      direccion: h.direccion || '',
      email: h.email || '',
      imagen: h.imagen || '',
      galeria: galeriaArr,
      panorama_destino_id: h.panorama_destino_id ?? null,
      ancho: Number(h.ancho) || (cssBase === 'hotspot-caja-logo' ? LOGO_FASCIA_ANCHO_DEFAULT : cssBase === 'hotspot-persona' ? PERSONA_ANCHO_DEFAULT : 300),
      alto: Number(h.alto) || (cssBase === 'hotspot-caja-logo' ? LOGO_FASCIA_ALTO_DEFAULT : cssBase === 'hotspot-persona' ? PERSONA_ALTO_DEFAULT : 200),
      scale_visual: Number(h.scale_visual) || 2.5
    });
    setEditingHotspotId(h.id);
    // La cámara se centra en el pitch/yaw de este hotspot al recrear el visor (mismo efecto que lista `editingHotspotId`)
  };

  const cancelEditHotspot = () => {
    setEditingHotspotId(null);
    setNuevoHotspot({
      pitch: lastClickCoordsRef.current?.pitch ?? 0,
      yaw: lastClickCoordsRef.current?.yaw ?? 0,
      css_class: 'hotspot-punto',
      color: '',
      flecha_estilo: 'simple',
      flecha_direccion: 'derecha',
      flecha_transform: { ...DEFAULT_FLECHA_TRANSFORM },
      url_externa: '',
      titulo: '',
      nombre: '',
      descripcion: '',
      link: '',
      telefono: '',
      telefono2: '',
      direccion: '',
      email: '',
      imagen: '',
        galeria: [],
        tieneNavegacion: false,
        panorama_destino_id: null,
        ancho: 300,
        alto: 200,
        scale_visual: 2.5
    });
  };

  const handleSaveHotspot = async () => {
    if (editingHotspotId != null) {
      const existente = hotspots.find(h => Number(h.id) === Number(editingHotspotId));
      const esFlecha = nuevoHotspot.css_class === 'hotspot-flecha-pulse';
      const esBurbuja = nuevoHotspot.css_class === 'hotspot-burbuja';
      const esPersona = nuevoHotspot.css_class === 'hotspot-persona';
      const esValla = nuevoHotspot.css_class === 'hotspot-valla';
      const esLogo = (nuevoHotspot.css_class || '').includes('hotspot-caja-logo');
      const tieneNavegacion = nuevoHotspot.tieneNavegacion && nuevoHotspot.panorama_destino_id;
      let cssClass = buildHotspotCssClass(nuevoHotspot);
      const pitchEnviar = Number(nuevoHotspot.pitch) || 0;
      const yawEnviar = Number(nuevoHotspot.yaw) || 0;
      try {
        const payload = {
          nombre: nuevoHotspot.nombre || null,
          titulo: nuevoHotspot.titulo || null,
          descripcion: nuevoHotspot.descripcion || null,
          pitch: pitchEnviar,
          yaw: yawEnviar,
          tipo: (esFlecha || esBurbuja) ? 'navegacion' : (esValla ? 'valla' : (esPersona ? 'persona' : (tieneNavegacion ? 'navegacion' : 'info'))),
          panorama_destino_id: (tieneNavegacion || esBurbuja) ? parseInt(nuevoHotspot.panorama_destino_id, 10) : null,
          tienda_id: existente?.tienda_id ?? null,
          url_externa: nuevoHotspot.url_externa || null,
          css_class: cssClass,
          imagen: nuevoHotspot.imagen || null,
          link: nuevoHotspot.link || null,
          link_panorama: nuevoHotspot.link_panorama || null,
          telefono: nuevoHotspot.telefono || null,
          telefono2: nuevoHotspot.telefono2 || null,
          direccion: nuevoHotspot.direccion || null,
          email: nuevoHotspot.email || null,
          galeria: (nuevoHotspot.galeria && nuevoHotspot.galeria.length > 0) ? JSON.stringify(nuevoHotspot.galeria) : null,
          transform_json: (esFlecha || esValla || esLogo || esPersona)
            ? JSON.stringify(
                esPersona
                  ? normalizePersonaTransformFromDb(nuevoHotspot.flecha_transform || {})
                  : (nuevoHotspot.flecha_transform || DEFAULT_FLECHA_TRANSFORM)
              )
            : null,
          ancho: esValla ? (Number(nuevoHotspot.ancho) || 300) : esLogo ? (Number(nuevoHotspot.ancho) || LOGO_FASCIA_ANCHO_DEFAULT) : esPersona ? (Number(nuevoHotspot.ancho) || PERSONA_ANCHO_DEFAULT) : null,
          alto: esValla ? (Number(nuevoHotspot.alto) || 200) : esLogo ? (Number(nuevoHotspot.alto) || LOGO_FASCIA_ALTO_DEFAULT) : esPersona ? (Number(nuevoHotspot.alto) || PERSONA_ALTO_DEFAULT) : null,
          scale_visual: esValla ? (Number(nuevoHotspot.scale_visual) || 2.5) : null
        };
        await api.put(`/api/hotspots/${editingHotspotId}`, {
          ...payload,
          orden: existente?.orden != null ? Number(existente.orden) : 0
        });
        await cargarCentro(centroId);
        cancelEditHotspot();
      } catch (err) {
        alert(err.response?.data?.message || err.message || 'Error al guardar');
      }
      return;
    }
    handleAddHotspot();
  };

  const getTipoLabel = (cssClass) => {
    if (!cssClass) return 'Punto';
    if (cssClass.includes('hotspot-flecha-pulse')) return 'Flecha';
    if (cssClass.includes('hotspot-burbuja')) return 'Burbuja';
    if (cssClass.includes('hotspot-caja-logo')) return 'Logo';
    if (cssClass.includes('hotspot-persona')) return 'Persona';
    if (cssClass.includes('hotspot-valla')) return 'Marquesina';
    return 'Punto';
  };

  const getNombreDestino = (destId) => {
    if (destId == null || destId === '') return null;
    const id = Number(destId);
    const p = panoramas.find((x) => Number(x.id) === id);
    return p ? etiquetaPanoramaLarga(p) : '???';
  };

  return (
    <div className="constructor-admin">
      <header className="constructor-header">
        <h1>Constructor de Centro Comercial</h1>
        <div>
          <a href="/admin/panel">← Panel</a>
          <a href="/admin/generar-360">Crear 360 con IA</a>
          {centroId && <a href={`/centro/${centroId}`} target="_blank" rel="noopener noreferrer">Ver recorrido</a>}
        </div>
      </header>

      <div className="constructor-body">
        <aside className="constructor-sidebar">
          <label>Centro comercial:</label>
          <select value={centroId || ''} onChange={e => cargarCentro(parseInt(e.target.value))}>
            <option value="">Seleccionar...</option>
            {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>

          {centroId && (
            <>
              <nav className="constructor-tabs">
                {['panoramas', 'plantas', 'pasillos', 'negocios', 'elementos'].map(t => (
                  <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
                    {t === 'panoramas' && '📷 Panoramas'}
                    {t === 'plantas' && '🏢 Plantas'}
                    {t === 'pasillos' && '🚶 Pasillos'}
                    {t === 'negocios' && '🏪 Negocios'}
                    {t === 'elementos' && '✨ Elementos'}
                  </button>
                ))}
              </nav>

              {tab === 'panoramas' && (
                <>
                  <h3>1. Panoramas (fotos 360º)</h3>
                  <p className="texto-paso">Sube las fotos del recorrido. Necesitas al menos 2 para poder enlazarlas en Pasillos.</p>
                  <label className="label-paso">Tipo</label>
                  <select value={uploadTipo} onChange={e => setUploadTipo(e.target.value)} className="input-paso">
                    {TIPOS_PANORAMA.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <label className="label-paso">Planta (opcional)</label>
                  <select value={uploadPlantaId} onChange={e => setUploadPlantaId(e.target.value)} className="input-paso">
                    <option value="">— Sin asignar —</option>
                    {plantas.map(pl => <option key={pl.id} value={pl.id}>{pl.nombre}</option>)}
                  </select>
                  <label className="btn-upload btn-block">
                    {uploading ? 'Subiendo...' : 'Subir foto 360º'}
                    <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} hidden />
                  </label>
                  <p className="label-paso" style={{ marginTop: 12 }}>Lista agrupada por planta · Clic para editar en Pasillos · Arrastra para ordenar dentro del centro</p>
                  <ul className="lista-panoramas">
                    {bloquesListaPanoramas.map((row) => {
                      if (row.type === 'header') {
                        return (
                          <li key={row.key} className="lista-panoramas-grupo-titulo" draggable={false}>
                            {row.label}
                          </li>
                        );
                      }
                      const p = row.p;
                      const idxOrden = panoramasOrdenados.findIndex((pp) => pp.id === p.id);
                      return (
                        <li
                          key={row.key}
                          draggable
                          onDragStart={(e) => {
                            setDragPanoramaIdx(idxOrden);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(idxOrden));
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const from = dragPanoramaIdx;
                            setDragPanoramaIdx(null);
                            const toIdx = panoramasOrdenados.findIndex((pp) => pp.id === p.id);
                            if (from !== null && toIdx >= 0 && from !== toIdx) reordenarPanoramas(from, toIdx);
                          }}
                          onDragEnd={() => setDragPanoramaIdx(null)}
                          className={dragPanoramaIdx === idxOrden ? 'dragging-panorama' : ''}
                        >
                          <span className="drag-handle-panorama" title="Arrastrar para reordenar">⋮⋮</span>
                          <button
                            type="button"
                            className={`panorama-list-btn ${editPanorama?.id === p.id ? 'active' : ''}`}
                            onClick={() => {
                              setEditPanorama(editPanorama?.id === p.id ? null : p);
                              setTab('pasillos');
                            }}
                          >
                            <span className="panorama-list-btn-titulo">{p.nombre || `Panorama ${p.id}`}</span>
                            <span className="panorama-list-btn-meta">
                              {(p.planta_id != null && p.planta_id !== ''
                                ? plantaPorId.get(Number(p.planta_id))?.nombre
                                : null) || 'Sin planta'}
                              {' · '}
                              {etiquetaTipoPanorama(p.tipo)}
                              {' · id '}
                              {p.id}
                              {' · orden '}
                              {Number(p.orden) || 0}
                            </span>
                          </button>
                          <span className="badge">{hotspotsDePanorama(p.id).length}</span>
                          <button
                            type="button"
                            className="btn-borrar"
                            title="Borrar esta foto 360"
                            onClick={async () => {
                              if (!window.confirm('¿Borrar esta foto 360 y sus enlaces?')) return;
                              try {
                                await api.delete(`/api/panoramas/${p.id}`);
                                if (editPanorama?.id === p.id) setEditPanorama(null);
                                cargarCentro(centroId);
                              } catch (err) {
                                alert(err.response?.data?.message || 'Error al borrar');
                              }
                            }}
                          >
                            ×
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {tab === 'plantas' && (
                <>
                  <h3>2. Plantas (pisos)</h3>
                  <p className="texto-paso">Crea los pisos del centro (Planta 0, Planta 1…) para organizar panoramas y tiendas.</p>
                  <input className="input-paso" placeholder="Nombre (ej: Planta 0)" value={nuevaPlanta.nombre} onChange={e => setNuevaPlanta(prev => ({ ...prev, nombre: e.target.value }))} />
                  <input type="number" className="input-paso" placeholder="Nivel" value={nuevaPlanta.nivel} onChange={e => setNuevaPlanta(prev => ({ ...prev, nivel: e.target.value }))} />
                  <button type="button" className="btn-upload btn-block" onClick={handleAddPlanta}>Añadir planta</button>
                  <ul className="lista-panoramas" style={{ marginTop: 12 }}>
                    {plantas.map(pl => (
                      <li key={pl.id}>
                        <span>{pl.nombre} (nivel {pl.nivel})</span>
                        <button type="button" onClick={async () => { await api.delete(`/api/plantas/${pl.id}`); cargarCentro(centroId); }} className="btn-borrar">×</button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {tab === 'negocios' && (
                <>
                  <h3>4. Negocios (tiendas)</h3>
                  <p className="texto-paso">Añade tiendas o negocios. Opcionalmente asígnalos a un panorama para que salgan en el recorrido.</p>
                  <input className="input-paso" placeholder="Nombre (ej: Zara)" value={nuevaTienda.nombre} onChange={e => setNuevaTienda(prev => ({ ...prev, nombre: e.target.value }))} />
                  <label className="label-paso">Planta (opcional)</label>
                  <select value={nuevaTienda.planta_id} onChange={e => setNuevaTienda(prev => ({ ...prev, planta_id: e.target.value }))} className="input-paso">
                    <option value="">— Ninguna —</option>
                    {plantas.map(pl => <option key={pl.id} value={pl.id}>{pl.nombre}</option>)}
                  </select>
                  <label className="label-paso">Panorama donde aparece (opcional)</label>
                  <select value={nuevaTienda.panorama_id} onChange={e => setNuevaTienda(prev => ({ ...prev, panorama_id: e.target.value }))} className="input-paso">
                    <option value="">— Ninguno —</option>
                    {panoramas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {etiquetaPanoramaSelect(p)}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn-upload btn-block" onClick={handleAddTienda}>Añadir negocio</button>
                  <ul className="lista-panoramas" style={{ marginTop: 12 }}>
                    {tiendas.map(t => <li key={t.id}><span>{t.nombre}</span></li>)}
                  </ul>
                </>
              )}

              {tab === 'elementos' && (
                <>
                  <h3>5. Elementos y personas</h3>
                  <p className="texto-paso">Puntos de información sobre una foto 360. Elige panorama, nombre y posición (clic en la imagen).</p>
                  <label className="label-paso">Panorama</label>
                  <select value={editPanorama?.id || ''} onChange={e => { const id = parseInt(e.target.value); setEditPanorama(panoramas.find(p => p.id === id) || null); }} className="input-paso">
                    <option value="">Seleccionar...</option>
                    {panoramas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {etiquetaPanoramaSelect(p)}
                      </option>
                    ))}
                  </select>
                  <label className="label-paso">Tipo</label>
                  <select value={nuevoElemento.tipo} onChange={e => setNuevoElemento(prev => ({ ...prev, tipo: e.target.value }))} className="input-paso">
                    <option value="elemento">Elemento / texto</option>
                    <option value="persona">Persona</option>
                  </select>
                  <input className="input-paso" placeholder="Nombre" value={nuevoElemento.nombre} onChange={e => setNuevoElemento(prev => ({ ...prev, nombre: e.target.value }))} />
                  <input className="input-paso" placeholder="Descripción (opcional)" value={nuevoElemento.descripcion} onChange={e => setNuevoElemento(prev => ({ ...prev, descripcion: e.target.value }))} />
                  <p className="texto-paso">Clic en la imagen a la derecha para fijar posición → luego “Añadir elemento aquí”.</p>
                  <button type="button" className="btn-upload btn-block" onClick={handleAddElemento} disabled={!editPanorama}>Añadir elemento aquí</button>
                </>
              )}

              {tab === 'pasillos' && (
                <div className="constructor-paso">
                  <h3>3. Pasillos (enlaces entre fotos)</h3>
                  <p className="texto-paso">
                    Enlaza fotos: el visitante hace clic en un punto y salta a otra foto 360. Elige una foto en <strong>Panoramas</strong> (cada ítem muestra planta, tipo e id); al activarla, arriba a la derecha verás la barra <strong>Foto 360 activa</strong> con el mismo contexto.
                  </p>
                  {panoramas.length < 2 && <p className="aviso">Necesitas al menos 2 panoramas. Sube otra foto en la pestaña Panoramas.</p>}
                  {editPanorama ? (
                    <div className="constructor-datos-panorama">
                      <h4>Datos de esta imagen 360</h4>
                      <p className="texto-paso" style={{ marginTop: 0 }}>
                        Ajusta nombre, planta, tipo y orden sin volver a subir el archivo. La vista de entrada guardada no se modifica.
                      </p>
                      <label className="label-paso">Nombre</label>
                      <input
                        className="input-paso"
                        value={editPanorama.nombre ?? ''}
                        onChange={(e) => setEditPanorama((prev) => (prev ? { ...prev, nombre: e.target.value } : prev))}
                        placeholder="Nombre descriptivo"
                      />
                      <label className="label-paso">Planta</label>
                      <select
                        className="input-paso"
                        value={editPanorama.planta_id != null && editPanorama.planta_id !== '' ? String(editPanorama.planta_id) : ''}
                        onChange={(e) =>
                          setEditPanorama((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  planta_id: e.target.value === '' ? null : Number(e.target.value)
                                }
                              : prev
                          )
                        }
                      >
                        <option value="">— Sin planta —</option>
                        {plantas.map((pl) => (
                          <option key={pl.id} value={pl.id}>
                            {pl.nombre}
                          </option>
                        ))}
                      </select>
                      <label className="label-paso">Tipo de escena</label>
                      <select
                        className="input-paso"
                        value={editPanorama.tipo || 'pasillo'}
                        onChange={(e) => setEditPanorama((prev) => (prev ? { ...prev, tipo: e.target.value } : prev))}
                      >
                        {TIPOS_PANORAMA.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <label className="label-paso">Orden (prioridad en listas)</label>
                      <input
                        type="number"
                        className="input-paso"
                        value={editPanorama.orden ?? 0}
                        onChange={(e) =>
                          setEditPanorama((prev) =>
                            prev ? { ...prev, orden: parseInt(e.target.value, 10) || 0 } : prev
                          )
                        }
                      />
                      <button type="button" className="btn-upload btn-block" style={{ marginTop: 16 }} onClick={guardarDatosPanoramaActivo}>
                        Guardar datos del panorama
                      </button>
                    </div>
                  ) : (
                    <p className="texto-paso aviso" style={{ marginTop: 14 }}>
                      Selecciona una foto en la pestaña <strong>Panoramas</strong> para poder editarla aquí.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </aside>

        <main className="constructor-main">
          {!editPanorama && (tab === 'pasillos' || tab === 'elementos') ? (
            <div className="constructor-empty">
              <p>Selecciona un panorama en la pestaña <strong>Panoramas</strong> para editar pasillos o colocar elementos.</p>
              <p>Las fotos 360º se conectan con hotspots de navegación; en Elementos puedes añadir puntos de información o personas.</p>
            </div>
          ) : (tab === 'pasillos' || tab === 'elementos') && editPanorama ? (
            <>
              <div className="constructor-panorama-contexto" role="status">
                <strong>Foto 360 activa:</strong>{' '}
                <span>{etiquetaPanoramaLarga(editPanorama)}</span>
                <span className="constructor-panorama-contexto-id"> · id {editPanorama.id}</span>
              </div>
              <div className="constructor-viewer-wrap" onClick={handleClickPanorama}>
                <div ref={viewerRef} className="constructor-panorama" />
                <div className="constructor-click-hint">Clic en la imagen para marcar dónde irá el enlace</div>
              </div>

              <div className="constructor-entrada-vista">
                <h4>Vista al entrar a esta escena (transición)</h4>
                <p className="texto-paso"><strong>Opcional.</strong> Coloca la cámara como quieras el encuadre y pulsa “Usar vista actual”; luego “Guardar vista”. Así se define qué parte del 360 se muestra al cargar o al llegar desde otra escena.</p>
                <div className="constructor-entrada-vista-grid">
                  <label>
                    Pitch (°)
                    <input type="number" step="0.1" value={entradaVista.pitch} onChange={(e) => setEntradaVista((prev) => ({ ...prev, pitch: parseFloat(e.target.value) || 0 }))} className="input-paso" />
                  </label>
                  <label>
                    Yaw (°)
                    <input type="number" step="0.1" value={entradaVista.yaw} onChange={(e) => setEntradaVista((prev) => ({ ...prev, yaw: parseFloat(e.target.value) || 0 }))} className="input-paso" />
                  </label>
                  <label>
                    Zoom (campo horizontal °)
                    <input type="number" min={50} max={120} step="1" value={entradaVista.hfov} onChange={(e) => setEntradaVista((prev) => ({ ...prev, hfov: Math.min(120, Math.max(50, parseInt(e.target.value, 10) || 120)) }))} className="input-paso" />
                  </label>
                </div>
                <div className="constructor-entrada-vista-actions">
                  <button type="button" className="btn-upload" onClick={usarVistaActualComoEntrada}>Usar vista actual del visor</button>
                  <button type="button" className="btn-upload" onClick={guardarVistaEntrada}>Guardar vista de entrada</button>
                  <button type="button" className="btn-cancel-edit" onClick={limpiarVistaEntrada}>Quitar vista guardada</button>
                </div>
              </div>

              <div className="constructor-hotspots-form">
                <h4>{editingHotspotId ? 'Editar hotspot' : 'Crear hotspot'}</h4>
                <p className="texto-paso"><strong>1.</strong> Clic en la foto para marcar posición → <strong>2.</strong> Completa los datos → <strong>3.</strong> Añade.</p>
                <p className="coords">Posición: Pitch {nuevoHotspot.pitch?.toFixed(1)}° · Yaw {nuevoHotspot.yaw?.toFixed(1)}° {(nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona') && '(clic en el 360 para ubicar; con imagen subida usa el marco azul para mover como el logo)'}</p>
                {editingHotspotId && (
                  <p className="texto-paso" style={{ color: '#0d6efd', fontWeight: 600 }}>
                    Modo edición: haz clic en otra zona de la imagen para mover este hotspot y luego pulsa "Guardar cambios".
                  </p>
                )}
                
                <label className="label-paso">Tipo de marcador</label>
                <select
                  className="input-paso"
                  value={nuevoHotspot.css_class || 'hotspot-punto'}
                  onChange={e => {
                      const v = e.target.value;
                      setNuevoHotspot(prev => ({
                        ...prev,
                        css_class: v,
                        tieneNavegacion: (v === 'hotspot-flecha-pulse' || v === 'hotspot-burbuja'),
                        flecha_transform: v === 'hotspot-persona'
                          ? { ...DEFAULT_PERSONA_TRANSFORM }
                          : prev.flecha_transform,
                        ancho: v === 'hotspot-caja-logo' ? LOGO_FASCIA_ANCHO_DEFAULT : v === 'hotspot-persona' ? PERSONA_ANCHO_DEFAULT : (prev.ancho ?? 300),
                        alto: v === 'hotspot-caja-logo' ? LOGO_FASCIA_ALTO_DEFAULT : v === 'hotspot-persona' ? PERSONA_ALTO_DEFAULT : (prev.alto ?? 200)
                      }));
                    }}
                >
                  {TIPOS_HOTSPOT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                {nuevoHotspot.css_class === 'hotspot-flecha-pulse' && (
                  <>
                    <p className="texto-paso" style={{ color: '#0d6efd', fontWeight: 600 }}>Flecha: indica el camino (pulse) y navega a la siguiente escena</p>
                    <label className="label-paso">Forma de flecha</label>
                    <select
                      className="input-paso"
                      value={nuevoHotspot.flecha_estilo || 'simple'}
                      onChange={e => setNuevoHotspot(prev => ({ ...prev, flecha_estilo: e.target.value }))}
                    >
                      {ESTILOS_FLECHA.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <label className="label-paso">Dirección de flecha</label>
                    <select
                      className="input-paso"
                      value={nuevoHotspot.flecha_direccion || 'derecha'}
                      onChange={e => setNuevoHotspot(prev => ({ ...prev, flecha_direccion: e.target.value }))}
                    >
                      {DIRECCIONES_FLECHA.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <div className="flecha-transform-panel">
                      <p className="texto-paso" style={{ marginTop: 0 }}>
                        Transformacion libre tipo Photoshop: arrastra el marco de la flecha para mover y usa
                        los puntos alrededor para escalar, distorsionar y rotar.
                      </p>
                      <button
                        type="button"
                        className="btn-cancel-edit"
                        onClick={() => setNuevoHotspot(prev => ({ ...prev, flecha_transform: { ...DEFAULT_FLECHA_TRANSFORM } }))}
                      >
                        Reset transformación
                      </button>
                    </div>
                    {nuevoHotspot.css_class !== 'hotspot-caja-logo' && (
                      <>
                        <label className="label-paso">Color de la flecha</label>
                        <select
                          className="input-paso"
                          value={nuevoHotspot.color || ''}
                          onChange={e => setNuevoHotspot(prev => ({ ...prev, color: e.target.value }))}
                        >
                          {COLORES_HOTSPOT.map(o => <option key={o.value || 'def'} value={o.value}>{o.label}</option>)}
                        </select>
                      </>
                    )}
                    <label className="label-paso">¿Ir a qué panorama? (requerido)</label>
                    <select
                      className="input-paso"
                      value={nuevoHotspot.panorama_destino_id ?? ''}
                      onChange={e => setNuevoHotspot(prev => ({ ...prev, panorama_destino_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                    >
                      <option value="">— Elige destino —</option>
                      {panoramas.filter(p => p.id !== editPanorama.id).map((p) => (
                        <option key={p.id} value={p.id}>
                          {etiquetaPanoramaSelect(p)}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {nuevoHotspot.css_class === 'hotspot-burbuja' && (
                  <>
                    <p className="texto-paso" style={{ color: '#0d6efd', fontWeight: 600 }}>
                      Burbuja: círculo con vista previa de la siguiente escena. El visitante hace clic para ir.
                    </p>
                    <label className="label-paso">Título (hacia dónde va)</label>
                    <input
                      className="input-paso"
                      placeholder="Ej.: Pasillo principal · Entrada norte"
                      value={nuevoHotspot.titulo || ''}
                      onChange={(e) => setNuevoHotspot((prev) => ({ ...prev, titulo: e.target.value }))}
                    />
                    <p className="texto-paso" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                      Se muestra debajo de la burbuja en el recorrido. Si lo dejas vacío, se usará el nombre del panorama destino.
                    </p>
                    <label className="label-paso">¿Qué panorama se muestra en la burbuja? (requerido)</label>
                    <select
                      className="input-paso"
                      value={nuevoHotspot.panorama_destino_id ?? ''}
                      onChange={e => setNuevoHotspot(prev => ({ ...prev, panorama_destino_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                    >
                      <option value="">— Elige panorama —</option>
                      {panoramas.filter(p => p.id !== editPanorama.id).map((p) => (
                        <option key={p.id} value={p.id}>
                          {etiquetaPanoramaSelect(p)}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {nuevoHotspot.css_class === 'hotspot-valla' && (
                  <>
                    <p className="texto-paso" style={{ color: '#0d6efd', fontWeight: 600 }}>
                      Marquesina MUPI: marco negro, pilar central y base. Arrastra el marco para mover, escalar, rotar y distorsionar (igual que la flecha).
                    </p>
                    <label className="label-paso">Imagen (requerido)</label>
                    <label className="btn-upload btn-block" style={{ cursor: subiendoImagenHotspot ? 'wait' : 'pointer' }}>
                      {subiendoImagenHotspot ? 'Subiendo...' : nuevoHotspot.imagen ? 'Archivo subido ✓' : 'Subir imagen rectangular'}
                      <input type="file" accept="image/*" onChange={handleUploadImagenHotspot} disabled={subiendoImagenHotspot} hidden />
                    </label>
                    {nuevoHotspot.imagen && <p style={{ fontSize: '0.85rem', color: '#28a745' }}>✓ {nuevoHotspot.imagen.split('/').pop()}</p>}
                    <label className="label-paso">Enlace (URL) — se abre en nueva pestaña</label>
                    <input className="input-paso" type="url" placeholder="https://..." value={nuevoHotspot.link || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, link: e.target.value }))} />
                    <label className="label-paso">Ancho (px)</label>
                    <input className="input-paso" type="number" min={80} max={800} value={nuevoHotspot.ancho ?? 300} onChange={e => setNuevoHotspot(prev => ({ ...prev, ancho: parseInt(e.target.value, 10) || 300 }))} />
                    <label className="label-paso">Alto (px)</label>
                    <input className="input-paso" type="number" min={60} max={600} value={nuevoHotspot.alto ?? 200} onChange={e => setNuevoHotspot(prev => ({ ...prev, alto: parseInt(e.target.value, 10) || 200 }))} />
                    <label className="label-paso">Tamaño en recorrido (1 = pequeño, 5 = grande)</label>
                    <input className="input-paso" type="number" min={1} max={5} step={0.1} value={nuevoHotspot.scale_visual ?? 2.5} onChange={e => setNuevoHotspot(prev => ({ ...prev, scale_visual: parseFloat(e.target.value) || 2.5 }))} />
                    <div className="flecha-transform-panel" style={{ marginTop: 12 }}>
                      <p className="texto-paso" style={{ marginTop: 0 }}>
                        Transformación libre: arrastra el marco para mover y usa los puntos para escalar, rotar y distorsionar.
                      </p>
                      <button type="button" className="btn-cancel-edit" onClick={() => setNuevoHotspot(prev => ({ ...prev, flecha_transform: { ...DEFAULT_FLECHA_TRANSFORM, scaleX: 1, scaleY: 1 } }))}>
                        Reset transformación
                      </button>
                    </div>
                  </>
                )}

                {(nuevoHotspot.css_class === 'hotspot-punto' || nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona') && (
                  <>
                    <p className="texto-paso" style={{ color: '#0d6efd', fontWeight: 600 }}>
                      {nuevoHotspot.css_class === 'hotspot-punto' && 'Punto: información + navegación opcional'}
                      {nuevoHotspot.css_class === 'hotspot-caja-logo' && 'Logo en mini stand: arriba el faldón blanco (tu imagen), abajo paredes y suelo tipo feria; sube el alto si el logo lo necesita. Tiradores: escalar, sesgar, rotar, perspectiva 3D.'}
                      {nuevoHotspot.css_class === 'hotspot-persona' && 'Persona/objeto en escena: PNG, GIF o WEBM. Tras subir la imagen verás el marco en el 360: arrastra para mover/ubicar, puntos para escalar, rotar y perspectiva (igual que el logo).'}
                    </p>
                    
                    {nuevoHotspot.css_class === 'hotspot-persona' && (
                      <div className="guia-personas" style={{ background: '#f0f7ff', border: '1px solid #b6d4fe', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: '0.85rem' }}>
                        <strong>¿De dónde saco la persona/objeto para la escena?</strong>
                        <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                          <li><strong>Generar con IA:</strong> <a href="https://replicate.com/explore?query=person%20transparent" target="_blank" rel="noopener noreferrer">Replicate</a>, <a href="https://leonardo.ai" target="_blank" rel="noopener noreferrer">Leonardo.ai</a> o <a href="https://www.midjourney.com" target="_blank" rel="noopener noreferrer">Midjourney</a>. Prompt ejemplo: &quot;person in business attire, full body, standing, transparent background, PNG, photorealistic&quot;. Descarga la imagen y súbela aquí.</li>
                          <li><strong>Animación:</strong> Sube un GIF o WEBM y se reproducirá en bucle en el recorrido.</li>
                          <li><strong>Objetos:</strong> También puedes subir PNG de macetas, bancas, muebles o displays para colocarlos como elementos en escena.</li>
                        </ul>
                      </div>
                    )}
                    
                    {nuevoHotspot.css_class === 'hotspot-punto' && (
                      <>
                        <label className="label-paso">Color del punto</label>
                        <select
                          className="input-paso"
                          value={nuevoHotspot.color || ''}
                          onChange={e => setNuevoHotspot(prev => ({ ...prev, color: e.target.value }))}
                        >
                          {COLORES_HOTSPOT.map(o => <option key={o.value || 'def'} value={o.value}>{o.label}</option>)}
                        </select>
                      </>
                    )}

                    {(nuevoHotspot.css_class === 'hotspot-caja-logo' || nuevoHotspot.css_class === 'hotspot-persona') && (
                      <>
                        <label className="label-paso">
                          {nuevoHotspot.css_class === 'hotspot-persona' ? 'Subir persona/objeto (PNG, GIF o WEBM, requerido)' : 'Subir logo/imagen (requerido)'}
                        </label>
                        <label className="btn-upload btn-block" style={{ cursor: subiendoImagenHotspot ? 'wait' : 'pointer' }}>
                          {subiendoImagenHotspot ? 'Subiendo...' : nuevoHotspot.imagen ? 'Archivo subido ✓' : (nuevoHotspot.css_class === 'hotspot-persona' ? 'Subir PNG, GIF o WEBM' : 'Subir imagen')}
                          <input type="file" accept={nuevoHotspot.css_class === 'hotspot-persona' ? 'image/*,video/webm,video/mp4' : 'image/*'} onChange={handleUploadImagenHotspot} disabled={subiendoImagenHotspot} hidden />
                        </label>
                        {nuevoHotspot.imagen && <p style={{ fontSize: '0.85rem', color: '#28a745' }}>✓ {nuevoHotspot.imagen.split('/').pop()}</p>}
                        {nuevoHotspot.css_class === 'hotspot-caja-logo' && (
                          <>
                            <label className="label-paso">Ancho (px)</label>
                            <input className="input-paso" type="number" min={40} max={900} value={nuevoHotspot.ancho ?? LOGO_FASCIA_ANCHO_DEFAULT} onChange={e => setNuevoHotspot(prev => ({ ...prev, ancho: parseInt(e.target.value, 10) || LOGO_FASCIA_ANCHO_DEFAULT }))} />
                            <label className="label-paso">Alto (px)</label>
                            <input className="input-paso" type="number" min={32} max={640} value={nuevoHotspot.alto ?? LOGO_FASCIA_ALTO_DEFAULT} onChange={e => setNuevoHotspot(prev => ({ ...prev, alto: parseInt(e.target.value, 10) || LOGO_FASCIA_ALTO_DEFAULT }))} />
                          </>
                        )}
                        {nuevoHotspot.css_class === 'hotspot-persona' && (
                          <>
                            <label className="label-paso">Ancho caja persona/objeto (px)</label>
                            <input className="input-paso" type="number" min={80} max={900} value={nuevoHotspot.ancho ?? PERSONA_ANCHO_DEFAULT} onChange={e => setNuevoHotspot(prev => ({ ...prev, ancho: parseInt(e.target.value, 10) || PERSONA_ANCHO_DEFAULT }))} />
                            <label className="label-paso">Alto caja persona/objeto (px)</label>
                            <input className="input-paso" type="number" min={120} max={900} value={nuevoHotspot.alto ?? PERSONA_ALTO_DEFAULT} onChange={e => setNuevoHotspot(prev => ({ ...prev, alto: parseInt(e.target.value, 10) || PERSONA_ALTO_DEFAULT }))} />
                          </>
                        )}
                        <div className="flecha-transform-panel" style={{ marginTop: 12 }}>
                          <p className="texto-paso" style={{ marginTop: 0 }}>
                            Arrastra el marco para mover, o usa los puntos para escalar, rotar, sesgar y distorsionar. El ajuste se ve igual en el recorrido.
                          </p>
                          <button
                            type="button"
                            className="btn-cancel-edit"
                            onClick={() =>
                              setNuevoHotspot((prev) => ({
                                ...prev,
                                flecha_transform:
                                  prev.css_class === 'hotspot-persona'
                                    ? { ...DEFAULT_PERSONA_TRANSFORM }
                                    : { ...DEFAULT_FLECHA_TRANSFORM, scaleY: 1 }
                              }))
                            }
                          >
                            Reset transformación
                          </button>
                        </div>
                      </>
                    )}

                    <label className="label-paso">Título</label>
                    <input className="input-paso" placeholder="Título del hotspot" value={nuevoHotspot.titulo || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, titulo: e.target.value }))} />
                    
                    <label className="label-paso">Nombre</label>
                    <input className="input-paso" placeholder="Nombre" value={nuevoHotspot.nombre || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, nombre: e.target.value }))} />
                    
                    <label className="label-paso">Descripción</label>
                    <textarea className="input-paso" rows="3" placeholder="Descripción (opcional)" value={nuevoHotspot.descripcion || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, descripcion: e.target.value }))} />
                    
                    <label className="label-paso">Link (URL) — se abrirá en nueva pestaña</label>
                    <input className="input-paso" type="url" placeholder="https://..." value={nuevoHotspot.link || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, link: e.target.value }))} />

                    <label className="label-paso">Link panorama 360 (botón ENTRAR en el modal)</label>
                    <p className="texto-paso" style={{ marginTop: 0 }}>URL del recorrido virtual 360 del negocio. Aparecerá como botón destacado «ENTRAR» en el popover del hotspot.</p>
                    <input className="input-paso" type="url" placeholder="https://..." value={nuevoHotspot.link_panorama || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, link_panorama: e.target.value }))} />
                    
                    <label className="label-paso">Teléfono</label>
                    <input className="input-paso" placeholder="Teléfono" value={nuevoHotspot.telefono || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, telefono: e.target.value }))} />
                    
                    <label className="label-paso">Teléfono 2</label>
                    <input className="input-paso" placeholder="Segundo teléfono (opcional)" value={nuevoHotspot.telefono2 || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, telefono2: e.target.value }))} />
                    
                    <label className="label-paso">Email</label>
                    <input className="input-paso" type="email" placeholder="email@ejemplo.com" value={nuevoHotspot.email || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, email: e.target.value }))} />
                    
                    <label className="label-paso">Dirección</label>
                    <input className="input-paso" placeholder="Dirección" value={nuevoHotspot.direccion || ''} onChange={e => setNuevoHotspot(prev => ({ ...prev, direccion: e.target.value }))} />

                    {(nuevoHotspot.css_class === 'hotspot-punto' || nuevoHotspot.css_class === 'hotspot-caja-logo') && (
                      <>
                        <label className="label-paso">Galería (varias imágenes)</label>
                        <p className="texto-paso" style={{ marginTop: 0 }}>Sube imágenes para mostrar en el popover. Se guardan en el servidor.</p>
                        <label className="btn-upload btn-block" style={{ cursor: subiendoImagenHotspot ? 'wait' : 'pointer', marginBottom: 8 }}>
                          {subiendoImagenHotspot ? 'Subiendo...' : 'Añadir imagen a la galería'}
                          <input type="file" accept="image/*" onChange={handleAddGaleriaImagen} disabled={subiendoImagenHotspot} hidden />
                        </label>
                        {(nuevoHotspot.galeria && nuevoHotspot.galeria.length > 0) && (
                          <div className="galeria-preview">
                            {nuevoHotspot.galeria.map((ruta, idx) => (
                              <div key={idx} className="galeria-item">
                                <img src={(ruta.startsWith('http') ? ruta : (API_BASE + (ruta.startsWith('/') ? ruta : '/' + ruta)))} alt="" />
                                <button type="button" className="galeria-quitar" onClick={() => setNuevoHotspot(prev => ({ ...prev, galeria: prev.galeria.filter((_, i) => i !== idx) }))} title="Quitar">×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {nuevoHotspot.css_class === 'hotspot-punto' && (
                      <>
                        <label className="label-paso" style={{ marginTop: '1rem' }}>
                          <input type="checkbox" checked={nuevoHotspot.tieneNavegacion} onChange={e => setNuevoHotspot(prev => ({ ...prev, tieneNavegacion: e.target.checked }))} />
                          {' '}También navegar a otra imagen
                        </label>
                        {nuevoHotspot.tieneNavegacion && (
                          <select
                            className="input-paso"
                            value={nuevoHotspot.panorama_destino_id ?? ''}
                            onChange={e => setNuevoHotspot(prev => ({ ...prev, panorama_destino_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                          >
                            <option value="">— Elige destino —</option>
                            {panoramas.filter(p => p.id !== editPanorama.id).map((p) => (
                              <option key={p.id} value={p.id}>
                                {etiquetaPanoramaSelect(p)}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    )}
                  </>
                )}

                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button" className="btn-add-hotspot" onClick={handleSaveHotspot}>
                    {editingHotspotId ? 'Guardar cambios' : 'Añadir hotspot'}
                  </button>
                  {editingHotspotId && (
                    <button type="button" className="btn-cancel-edit" onClick={cancelEditHotspot}>
                      Cancelar edición
                    </button>
                  )}
                </div>
              </div>

              <div className="constructor-hotspots-list">
                <h4>Hotspots en este panorama</h4>
                <p className="texto-paso" style={{ marginTop: 0 }}>Arrastra para cambiar el orden de prioridad (p. ej. en listas).</p>
                {hotspotsDePanorama(editPanorama.id).length === 0 ? (
                  <p>Aún no hay. Haz clic en la imagen, elige destino y añade.</p>
                ) : (
                  <ul>
                    {hotspotsDePanorama(editPanorama.id).map((h, hi) => (
                      <li
                        key={h.id}
                        className={`${editingHotspotId === h.id ? 'editing' : ''} hotspot-list-item-draggable`}
                        draggable
                        onDragStart={(e) => {
                          dragHotspotIdRef.current = h.id;
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = hotspotsDePanorama(editPanorama.id).findIndex((x) => x.id === dragHotspotIdRef.current);
                          dragHotspotIdRef.current = null;
                          if (from >= 0 && from !== hi) reordenarHotspots(from, hi);
                        }}
                        onDragEnd={() => { dragHotspotIdRef.current = null; }}
                      >
                        <span className="drag-handle-hotspot" title="Arrastrar">⋮⋮</span>
                        <strong>{getTipoLabel(h.css_class)}</strong>
                        {' — '}
                        {h.titulo || h.nombre || '(sin título)'}
                        {getNombreDestino(h.panorama_destino_id) != null && (
                          <> → {getNombreDestino(h.panorama_destino_id)}</>
                        )}
                        <span className="hotspot-actions">
                          <button type="button" className="btn-edit-hotspot" onClick={() => startEditHotspot(h)} title="Editar">Editar</button>
                          <button type="button" className="btn-delete-hotspot" onClick={async () => {
                            await api.delete(`/api/hotspots/${h.id}`);
                            cargarCentro(centroId);
                            if (editingHotspotId === h.id) cancelEditHotspot();
                          }} title="Eliminar">×</button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {tab === 'elementos' && (
                <div className="constructor-hotspots-form" style={{ marginTop: 16 }}>
                  <h4>Elemento / persona en esta posición</h4>
                  <p>Pitch: {nuevoElemento.pitch?.toFixed(1)}° | Yaw: {nuevoElemento.yaw?.toFixed(1)}° (clic en la imagen para cambiar)</p>
                  <button onClick={handleAddElemento} disabled={!nuevoElemento.nombre?.trim()}>+ Añadir elemento aquí</button>
                </div>
              )}
            </>
          ) : tab === 'plantas' || tab === 'negocios' ? (
            <div className="constructor-empty">
              <p>Usa el panel izquierdo para crear plantas (pisos) o negocios/tiendas del centro.</p>
            </div>
          ) : (
            <div className="constructor-empty">
              <p>Selecciona un centro y usa las pestañas para gestionar panoramas, plantas, pasillos, negocios y elementos.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ConstructorAdmin;
